import { web3 } from "@project-serum/anchor";
import BN from "bn.js";
import {
  deserializeBorsh,
  getGovernance,
  getProposal,
  getRealm,
  getSignatoryRecordAddress,
  getVoteRecordsByVoter,
  Governance,
  GOVERNANCE_SCHEMA,
  ProgramAccount,
  Proposal,
  ProposalTransaction,
  Vote,
  VoteRecord,
  VoteType,
  withAddSignatory,
  withCancelProposal,
  withCastVote,
  withCreateProposal,
  withSignOffProposal,
} from "@solana/spl-governance";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  NATIVE_MINT,
  Token,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import { encode } from "@project-serum/anchor/dist/cjs/utils/bytes/bs58";
import { chunk } from "loadsh";
import {
  AccountMeta,
  ComputeBudgetProgram,
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";

import {
  CHAIN_ID,
  EMPTY_STRING,
  MESSAGE_TYPE,
  SPL_PROGRAM_VERSION,
  TREASURY_INDEX,
} from "../../common/constants/common.constants";
import { ClubType, SequenceType } from "../../common/enums/club.enum";
import {
  OfferStatus,
  ProposalAction,
  ProposalStatus,
  ProposalTypeForProgram,
  ProposalTypeProgramString,
} from "../../common/enums/proposals.enums";
import {
  IClubTreasury,
  IMembersRights,
  IReservedRights,
  ISellPermissionConfig,
  ITreasuryTokenAccInfo,
} from "../../common/interfaces/clubs.interface";

import {
  IBasicProposalData,
  INftsWithTradeInfo,
  IOffer,
  IProposalWithMetaData,
} from "../../common/interfaces/proposals.interface";
import {
  getMintDecimalsDivider,
  getTreasuryCount,
} from "../../utilities/helpers";
import { createNotification } from "../../utilities/notification";
import {
  createTokenAccountOwnedBySpecifiedAccount,
  getInfoForMintAccounts,
  writeValueToNewInt32Buffer,
} from "../common/helpers/common.helpers";
import {
  acceptOfferSeed,
  accountGovernanceSeed,
  escrowProgramFactory,
  escrowSeed,
  ESCRROW_PROGRAM,
  governanceSeed,
  magicEdenSeed,
  maxVoterWeightSeed,
  METADATA_PROGRAM_ID,
  metadataSeed,
  nftVoteRecordSeed,
  offerSeed,
  profitSeed,
  programFactory,
  proposalMetadataSeed,
  realmConfigSeed,
  RPC_CONNECTION,
  splGovernanceProgram,
  tokenLedgerSeed,
  transferProfitSeed,
  treasuryDataSeed,
  treasurySeed,
  unqClubMemberSeed,
  unqClubSeed,
  vaultSeed,
  voterWeightSeed,
  withdrawalDataSeed,
  withdrawalRecordSeed,
  withdrawalSeed,
} from "../common/utilities";
import { sendTransaction } from "../transactions/sendTransaction";
import { sendTransactions } from "../transactions/sendTransactions";
import { getRemainingAccountForMemberOfNFTClub } from "../common/helpers/nftClubs.helpers";
import {
  getAccountGovernanceForTradingProposal,
  getProposalMetaData,
  mapProposalAction,
  mapProposalTypeFromProgram,
  prepareTransferProfitIx,
} from "../common/helpers/proposals.helpers";
import { ClubAction } from "../../common/enums/common.enums";
import {
  ICreateTokenAcc,
  IDetailNftData,
  IInstructionSet,
} from "../../common/interfaces/common.interface";
import { buyNowMagicEden, sellMagicEden } from "../../api/magic-eden.api";
import {
  compareBuyPrices,
  getCreateProposalMetadataRAForBuyNow,
  getCreateProposalMetadataRAForSell,
  getExecuteMeTransactionAccounts,
  getExecuteMeTransactionRemainingAccounts,
  getMagicEdenExecuteSell,
  makeSellMagicEdenDto,
} from "../common/helpers/magic-eden.helpers";
import useTransactionStore from "../../contexts/transactionStore";
import { ICancellationReasonDto } from "../../common/interfaces/club-application.interface";
import { saveCancellationReason } from "../../api/proposals.api";
import { divide, pow } from "mathjs";
import { getFinancialOfferPDAs, getFinancialRecordPDA } from "./treasury";

//TODO@milica@guta: Treasury index is set to 0, but when we implement multiple treasuries keep in mind to change this number

/**
 * Create Treasury/Vault related proposal
 * @param name {string} Proposal name
 * @param description {string} Proposal description
 * @param discussionLink {string} Discussion link
 * @param realmName {string} Club name
 * @param action {ProposalAction} Action (Type) of proposal
 * @param offeredAmount {number} offered amount - in case of Sell Proposal 1
 * @param wantedAmount {number}  offered amount - in case of Buy Proposal 1
 * @param payer {AnchorWallet} User's wallet
 * @param clubTreasury {Club Treasury} Club treasury
 * @param nftMint {string} Mint of NFT
 * @param dedicatedTaker {string} Public key of dedicated taker if exist
 * @param mint {PublicKey} Community mint
 * @param clubType {ClubType} Club type
 * @param userRole {UserRole} Member's role in the club
 * @param nftTokenAccount? {<PublicKey>} Pub key of nft token account in case of Sell Proposal
 * @param sellPermissions? {ISellPermissionConfig[]} Sell permission array provided if club has sell permissions
 * @param nftMetadata? {<PublicKey>}  In case of NFT club, metadata of member's random nft from club's collection
 * @param membersNftTokenAccount? {<PublicKey>} In case of NFT club, token account of member's random nft from club's collection
 */
export async function createTreasuryVaultRelatedProposal(
  name: string,
  description: string,
  discussionLink: string,
  realmName: string,
  action: ProposalAction,
  offeredAmount: number,
  wantedAmount: number,
  payer: AnchorWallet,
  clubTreasury: IClubTreasury,
  nftMint: PublicKey,
  priceMint: PublicKey,
  dedicatedTaker: string,
  communityTokenMint: PublicKey,
  clubType: ClubType,
  userRole: string,
  nftTokenAccount?: PublicKey,
  sellPermissions?: ISellPermissionConfig[],
  nftMetadata?: PublicKey,
  membersNftTokenAccount?: PublicKey,
  isMarketplace?: boolean,
  existingOffer?: PublicKey,
  existingOfferMaker?: PublicKey
) {
  const clubProgram = programFactory();

  const countBuffer = Buffer.alloc(4);
  countBuffer.writeInt32LE(clubTreasury.fundraiseCount, 0);
  const remainingAccounts: AccountMeta[] = [];
  const allInstructions: IInstructionSet[] = [];
  const createProposalRemainigAccounts: AccountMeta[] = [];

  let makerWantedTokenAccount: PublicKey;
  let makerOfferedTokensAccount: PublicKey;
  let offeredTokenMint: PublicKey;
  let wantedTokenMint: PublicKey;

  const optionIndex = 0;
  const transactionIndex = 0;
  const optionIndexBuffer = Buffer.alloc(1);
  optionIndexBuffer.writeUInt8(optionIndex, 0);
  const instructionIndexBuffer = Buffer.alloc(2);
  instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

  const [realmPda] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmPda.toBuffer()],
    clubProgram.programId
  );

  const [vaultPda] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      clubTreasury.treasury.toBuffer(),
      vaultSeed,
      Buffer.from(CHAIN_ID),
    ],
    clubProgram.programId
  );

  //Token account for treasury with native mint
  //Try to find tokenAccount with selected mint
  //If doesnt exists, check if mint is Native and native createTokenAccount, else throw Error
  let tokenAccount: PublicKey | undefined = undefined;

  const treasuryTokenAccounts =
    await RPC_CONNECTION.getParsedTokenAccountsByOwner(clubTreasury.treasury, {
      mint: priceMint,
    });

  if (treasuryTokenAccounts.value.length > 0) {
    tokenAccount = treasuryTokenAccounts.value[0].pubkey;
  } else {
    if (priceMint.toString() === NATIVE_MINT.toString()) {
      const {
        tokenAccount: treasuryTA,
        createAccountIx,
        createInitAccountInstruction,
      } = await createTokenAccountOwnedBySpecifiedAccount(
        payer.publicKey,
        clubTreasury.treasury,
        priceMint
      );
      tokenAccount = treasuryTA.publicKey;
      allInstructions.push({
        instructions: [createAccountIx, createInitAccountInstruction],
        partialSigner: treasuryTA,
        description: "Create token account",
      });
    } else {
      throw new Error("Token account with selected mint doesnt exist");
    }
  }

  const denomMint =
    clubTreasury.denominatedTokenInfo?.mint.toString() ??
    NATIVE_MINT.toString();

  if (action === ProposalAction.BuyNft) {
    makerOfferedTokensAccount = tokenAccount;
    const vaultTokenAccount = await createTokenAccountOwnedBySpecifiedAccount(
      payer.publicKey,
      vaultPda,
      nftMint
    );

    makerWantedTokenAccount = vaultTokenAccount.tokenAccount.publicKey;
    wantedTokenMint = nftMint;

    offeredTokenMint = priceMint;

    allInstructions.push({
      instructions: [
        vaultTokenAccount.createAccountIx,
        vaultTokenAccount.createInitAccountInstruction,
      ],
      partialSigner: vaultTokenAccount.tokenAccount,
      description: "Create token account owned by vault",
    });
  } else {
    makerOfferedTokensAccount = nftTokenAccount!;
    makerWantedTokenAccount = tokenAccount;
    offeredTokenMint = nftMint;
    wantedTokenMint = priceMint;
  }

  const [defaultAccountGovernancePda] = await PublicKey.findProgramAddress(
    [
      accountGovernanceSeed,
      realmPda.toBuffer(),
      clubTreasury.treasury.toBuffer(),
    ],
    splGovernanceProgram
  );

  const {
    accountGovernancePda,
    useDeny,
    isSellPermissionFromZeroAndUserIsFounder,
  } = await getAccountGovernanceForTradingProposal(
    sellPermissions,
    action === ProposalAction.BuyNft ? offeredAmount : wantedAmount,
    realmPda,
    clubProgram,
    clubTreasury,
    defaultAccountGovernancePda,
    userRole,
    action === ProposalAction.BuyNft
      ? offeredTokenMint.toString() === denomMint
      : wantedTokenMint.toString() === denomMint
  );

  const accountGovernance = await getGovernance(
    RPC_CONNECTION,
    accountGovernancePda
  );

  const proposalIndexBuffer = Buffer.alloc(4);
  proposalIndexBuffer.writeInt32LE(accountGovernance.account.proposalCount, 0);
  const [proposalAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      accountGovernancePda.toBuffer(),
      communityTokenMint.toBuffer(),
      proposalIndexBuffer,
    ],
    splGovernanceProgram
  );

  const [instructionAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      proposalAddress.toBuffer(),
      optionIndexBuffer,
      instructionIndexBuffer,
    ],
    splGovernanceProgram
  );

  const [offer] = await PublicKey.findProgramAddress(
    [offerSeed, proposalAddress.toBuffer()],
    ESCRROW_PROGRAM
  );

  const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      realmPda.toBuffer(),
      communityTokenMint.toBuffer(),
      payer.publicKey.toBuffer(),
    ],
    splGovernanceProgram
  );

  const [realmConfigPda] = await PublicKey.findProgramAddress(
    [realmConfigSeed, realmPda.toBuffer()],
    splGovernanceProgram
  );

  const [proposalMetadata] = await PublicKey.findProgramAddress(
    [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
    clubProgram.programId
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      payer.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [escrowedWantedTokenAccount] = await PublicKey.findProgramAddress(
    [escrowSeed, offer.toBuffer(), acceptOfferSeed],
    ESCRROW_PROGRAM
  );

  try {
    const { updateVW, voterWeightAddress } = await updateVoterWeight(
      realmPda,
      payer,
      clubTreasury.treasury,
      clubTreasury.fundraiseCount,
      ClubAction.CreateP2PProposal,
      clubType,
      proposalAddress,
      isSellPermissionFromZeroAndUserIsFounder
    );

    const createProposalMetadataInstruction =
      clubProgram.instruction.createProposalMetadata(
        action === ProposalAction.BuyNft
          ? ProposalTypeForProgram.BuyP2P
          : ProposalTypeForProgram.SellP2P,
        null,
        null,
        null,
        {
          accounts: {
            realm: realmPda,
            clubData,
            proposal: proposalAddress,
            proposalMetadata,
            memberData: memberPDA,
            governance: accountGovernancePda,
            treasuryData: clubTreasury.treasuryDataAddress,
            payer: payer.publicKey,
            systemProgram: web3.SystemProgram.programId,
          },
        }
      );

    const updateProposalDescriptionIx =
      clubProgram.instruction.updateProposalDescription(
        description,
        name,
        ["option YES"],
        discussionLink,
        {
          accounts: {
            proposal: proposalAddress,
            proposalMetadata,
            payer: payer.publicKey,
          },
        }
      );

    const [escrowedOfferedTokens] = await PublicKey.findProgramAddress(
      [escrowSeed, (existingOffer ?? offer).toBuffer()],
      ESCRROW_PROGRAM
    );

    //TODO@all:use this when we implement marketplace
    // if (isMarketplace) {
    //   const [escrowWantedTokens] = await PublicKey.findProgramAddress(
    //     [escrowSeed, existingOffer!.toBuffer(), acceptOfferSeed],
    //     ESCRROW_PROGRAM
    //   );
    //   createProposalRemainigAccounts.push({
    //     isSigner: false,
    //     isWritable: true,
    //     pubkey: existingOffer!,
    //   });
    //   createProposalRemainigAccounts.push({
    //     isSigner: false,
    //     isWritable: true,
    //     pubkey: escrowWantedTokens!,
    //   });
    //   createProposalRemainigAccounts.push({
    //     isSigner: false,
    //     isWritable: true,
    //     pubkey: existingOfferMaker!,
    //   });
    // }

    const createProposalInstruction =
      clubProgram.instruction.createClubProposal(
        CHAIN_ID,
        useDeny,
        new BN(offeredAmount),
        new BN(wantedAmount),
        action,
        dedicatedTaker !== EMPTY_STRING ? new PublicKey(dedicatedTaker) : null,
        {
          accounts: {
            governance: accountGovernancePda,
            realm: realmPda,
            governanceAuthority: payer.publicKey,
            realmConfig: realmConfigPda,
            proposal: proposalAddress,
            proposalTransactionAddress: instructionAddress,
            tokenOwnerRecord: tokenOwnerRecord,
            splGovernanceProgram,
            communityTokenMint: communityTokenMint,
            payer: payer.publicKey,
            voterWeightRecord: voterWeightAddress,
            offer,
            makerOfferedTokens: makerOfferedTokensAccount,
            escrowedWantedTokens: escrowedWantedTokenAccount,
            makerOfferedAuthority:
              action === ProposalAction.BuyNft
                ? clubTreasury.treasury
                : vaultPda,
            makerWantedAuthority:
              action === ProposalAction.BuyNft
                ? vaultPda
                : clubTreasury.treasury,
            treasuryData: clubTreasury.treasuryDataAddress,
            proposalMetadata,
            clubData,
            wantedTokensMint: wantedTokenMint,
            systemProgram: web3.SystemProgram.programId,
            rent: web3.SYSVAR_RENT_PUBKEY,
          },
          remainingAccounts: createProposalRemainigAccounts,
        }
      );

    allInstructions.push({
      instructions: [createProposalMetadataInstruction],
      description: "Create proposal metadata",
    });

    allInstructions.push({
      instructions: [updateProposalDescriptionIx],
      description: "Add proposal description",
    });

    allInstructions.push({
      instructions: [updateVW, createProposalInstruction],
      description: "Create proposal account, update voter weight for user",
    });

    if (
      isSellPermissionFromZeroAndUserIsFounder &&
      nftMetadata &&
      membersNftTokenAccount
    ) {
      const castVoteIx: TransactionInstruction[] = [];

      const { updateVW, voterWeightAddress } = await updateVoterWeight(
        realmPda,
        payer,
        clubTreasury.treasury,
        clubTreasury.fundraiseCount,
        ClubAction.CastVote,
        clubType,
        proposalAddress,
        isSellPermissionFromZeroAndUserIsFounder
      );

      castVoteIx.push(updateVW);
      const [maxVoterWeightPda] = await PublicKey.findProgramAddress(
        [unqClubSeed, proposalAddress.toBuffer(), maxVoterWeightSeed],
        clubProgram.programId
      );

      await withCastVote(
        castVoteIx,
        splGovernanceProgram,
        SPL_PROGRAM_VERSION,
        realmPda,
        accountGovernancePda,
        proposalAddress,
        tokenOwnerRecord,
        tokenOwnerRecord,
        payer.publicKey, //Governance authority - vote authority
        communityTokenMint,
        Vote.fromYesNoVote(0),
        payer.publicKey,
        voterWeightAddress,
        maxVoterWeightPda
      );

      allInstructions.push({
        instructions: [...castVoteIx],
        description: "Cast vote",
      });

      const [escrowedOfferedTokensAccountPda] =
        await PublicKey.findProgramAddress(
          [escrowSeed, offer.toBuffer()],
          ESCRROW_PROGRAM
        );

      const [vaultAddress] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          clubData.toBuffer(),
          clubTreasury.treasury.toBuffer(),
          vaultSeed,
          Buffer.from(CHAIN_ID),
        ],
        clubProgram.programId
      );

      const [proposalMetadata] = await PublicKey.findProgramAddress(
        [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
        clubProgram.programId
      );

      const executeProposalIxs: TransactionInstruction[] = [];

      if (action === ProposalAction.BuyNft) {
        const transferProfitIxs = await prepareTransferProfitIx(
          clubTreasury.treasury,
          proposalAddress,
          offeredTokenMint,
          payer,
          offeredAmount,
          clubData
        );

        if (transferProfitIxs.transfeProfitIx) {
          if (transferProfitIxs.allInstructions.length > 0) {
            allInstructions.push(transferProfitIxs.allInstructions[0]);
          }
          executeProposalIxs.push(transferProfitIxs.transfeProfitIx);
        }
      }
      const executeProposalInstruction =
        clubProgram.instruction.executeTransaction(
          CHAIN_ID,
          action,
          TREASURY_INDEX + 1,
          {
            accounts: {
              clubData,
              governance: accountGovernancePda,
              realm: realmPda,
              proposalMetadata,
              proposal: proposalAddress,
              proposalTransaction: instructionAddress,
              splGovernanceProgram,
              payer: payer.publicKey,
              offer,
              makerOfferedTokens: makerOfferedTokensAccount,
              escrowedWantedTokenAccount,
              maker:
                action === ProposalAction.BuyNft
                  ? clubTreasury.treasury
                  : vaultAddress,
              escrowedOfferedTokens: escrowedOfferedTokensAccountPda,
              offeredTokensMint: offeredTokenMint,
              wantedTokensMint: wantedTokenMint,
              escrowProgram: ESCRROW_PROGRAM,
              treasury: clubTreasury.treasury,
              tokenProgram: TOKEN_PROGRAM_ID,
              systemProgram: web3.SystemProgram.programId,
              rent: web3.SYSVAR_RENT_PUBKEY,
            },
          }
        );
      executeProposalIxs.push(executeProposalInstruction);

      allInstructions.push({
        instructions: executeProposalIxs,
        description: "Execute proposal",
      });
    }

    await sendTransactions(
      RPC_CONNECTION,
      payer,
      allInstructions,
      SequenceType.Sequential,
      isSellPermissionFromZeroAndUserIsFounder
    );
    return proposalAddress;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Create Withdrawal proposal
 * @param name {string} Proposal name
 * @param description {string} Proposal description
 * @param discussionLink {string} Discussion link
 * @param realmName {string} Realm name
 * @param payer {AnchorWallet} Payer's wallet
 * @param clubTreasury {IClubTreasury} Club's treasury
 * @param mint {PublicKey} Collection mint
 * @param clubType {clubType} Club type
 * @param withdrawalAmount {number} Withdrawal amount
 * @param nftMetadata? {PublicKey}  In case of NFT club, metadata of member's random nft from club's collection
 * @param membersNftTokenAccount? {PublicKey} In case of NFT club, token account of member's random nft from club's collection
 */
export async function createWithdrawalProposal(
  name: string,
  description: string,
  discussionLink: string,
  realmName: string,
  payer: AnchorWallet,
  clubTreasury: IClubTreasury,
  mint: PublicKey,
  clubType: ClubType,
  withdrawalAmount: BN,
  treasuryToken: ITreasuryTokenAccInfo,
  nftMetadata?: PublicKey,
  membersNftTokenAccount?: PublicKey
) {
  try {
    const clubProgram = programFactory();
    const escrowProgram = escrowProgramFactory();
    const allInstructions: IInstructionSet[] = [];
    let treasuryTokenAddress = treasuryToken.tokenAddress;

    const optionIndex = 0;
    const transactionIndex = 0;
    const optionIndexBuffer = Buffer.alloc(1);
    optionIndexBuffer.writeUInt8(optionIndex, 0);

    const countBuffer = Buffer.alloc(4);
    countBuffer.writeInt32LE(clubTreasury.fundraiseCount, 0);

    const instructionIndexBuffer = Buffer.alloc(2);
    instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

    const [realmPda] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmPda.toBuffer()],
      clubProgram.programId
    );

    if (
      treasuryToken.mint.toString() === NATIVE_MINT.toString() &&
      treasuryToken.tokenAddress === undefined
    ) {
      const { tokenAccount, createAccountIx, createInitAccountInstruction } =
        await createTokenAccountOwnedBySpecifiedAccount(
          payer.publicKey,
          clubTreasury.treasury,
          NATIVE_MINT
        );
      allInstructions.push({
        instructions: [createAccountIx, createInitAccountInstruction],
        partialSigner: tokenAccount,
        description: "Create token account",
      });
      treasuryTokenAddress = tokenAccount.publicKey;
    }
    const [accountGovernancePda] = await PublicKey.findProgramAddress(
      [
        accountGovernanceSeed,
        realmPda.toBuffer(),
        clubTreasury.treasury.toBuffer(),
      ],
      splGovernanceProgram
    );

    const accountGovernance = await getGovernance(
      RPC_CONNECTION,
      clubTreasury.withdrawalGovernance ?? accountGovernancePda
    );
    const proposalIndexBuffer = Buffer.alloc(4);
    proposalIndexBuffer.writeInt32LE(
      accountGovernance.account.proposalCount,
      0
    );

    const [proposalAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        clubTreasury.withdrawalGovernance?.toBuffer() ??
          accountGovernancePda.toBuffer(),
        mint.toBuffer(),
        proposalIndexBuffer,
      ],
      splGovernanceProgram
    );

    const [instructionAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        proposalAddress.toBuffer(),
        optionIndexBuffer,
        instructionIndexBuffer,
      ],
      splGovernanceProgram
    );
    const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        realmPda.toBuffer(),
        mint.toBuffer(),
        payer.publicKey.toBuffer(),
      ],
      splGovernanceProgram
    );
    const [realmConfigPda] = await PublicKey.findProgramAddress(
      [realmConfigSeed, realmPda.toBuffer()],
      splGovernanceProgram
    );

    const [proposalMetadata] = await PublicKey.findProgramAddress(
      [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
      clubProgram.programId
    );

    const [withdrawal] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        proposalAddress.toBuffer(),
        clubTreasury.treasury.toBuffer(),
        withdrawalSeed,
      ],
      escrowProgram.programId
    );

    const [withdrawalData] = await PublicKey.findProgramAddress(
      [unqClubSeed, withdrawal.toBuffer(), withdrawalDataSeed],
      escrowProgram.programId
    );

    const [memberPDA] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        payer.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    const { updateVW, voterWeightAddress } = await updateVoterWeight(
      realmPda,
      payer,
      clubTreasury.treasury,
      clubTreasury.fundraiseCount,
      ClubAction.CreateWithdrawalProposal,
      clubType,
      proposalAddress
    );

    //TODO@all:check withdrawal mint later
    const createProposalMetadataInstruction =
      clubProgram.instruction.createProposalMetadata(
        ProposalTypeForProgram.Withdrawal,
        null,
        null,
        null,
        {
          accounts: {
            clubData,
            realm: realmPda,
            proposal: proposalAddress,
            proposalMetadata,
            memberData: memberPDA,
            governance: accountGovernance.pubkey,
            treasuryData: clubTreasury.treasuryDataAddress,
            payer: payer.publicKey,
            systemProgram: web3.SystemProgram.programId,
          },
        }
      );

    const updateProposalDescriptionIx =
      clubProgram.instruction.updateProposalDescription(
        description,
        name,
        ["option YES"],
        discussionLink,
        {
          accounts: {
            proposal: proposalAddress,
            proposalMetadata,
            payer: payer.publicKey,
          },
        }
      );

    const createWithdrawalProposalIx =
      clubProgram.instruction.createWithdrawalProposal(true, withdrawalAmount, {
        accounts: {
          governance: clubTreasury.withdrawalGovernance ?? accountGovernancePda,
          clubData,
          realm: realmPda,
          realmConfig: realmConfigPda,
          proposal: proposalAddress,
          proposalTransactionAddress: instructionAddress,
          tokenOwnerRecord,
          splGovernanceProgram,
          communityTokenMint: mint,
          payer: payer.publicKey,
          voterWeightRecord: voterWeightAddress,
          treasury: clubTreasury.treasury,
          treasuryData: clubTreasury.treasuryDataAddress,
          treasuryToken: treasuryTokenAddress!,
          withdrawal,
          withdrawalData,
          withdrawalMint: treasuryToken.mint,
          proposalMetadata,
          systemProgram: web3.SystemProgram.programId,
          rent: web3.SYSVAR_RENT_PUBKEY,
          tokenProgram: TOKEN_PROGRAM_ID,
        },
      });

    allInstructions.push({
      instructions: [createProposalMetadataInstruction],
      description: "Create proposal metadata",
    });

    allInstructions.push({
      instructions: [updateProposalDescriptionIx],
      description: "Add proposal description",
    });

    allInstructions.push({
      instructions: [updateVW, createWithdrawalProposalIx],
      description: "Create proposal account, update voter weight for user",
    });

    await sendTransactions(
      RPC_CONNECTION,
      payer,
      allInstructions,
      SequenceType.Sequential,
      true
    );

    return {
      proposalAddress,
      accountGovernanceIndex: accountGovernance.account.proposalCount,
      // TODO@milica-monke-merge: nftMetadata && membersNftTokenAccount is only for monke
      isZeroQuorum:
        accountGovernance.account.config.voteThresholdPercentage.value === 0 &&
        nftMetadata &&
        membersNftTokenAccount,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Get all proposals for selected club
 * @param realmName {string} Club name
 */
export async function getProposalsForRealm(realmName: string) {
  const clubProgram = programFactory();
  const basicProposalsData: IBasicProposalData[] = [];

  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const proposalMetadatas = await clubProgram.account.proposalMetadata.all();

  const filteredMetadatas = proposalMetadatas.filter(
    (p) => p.account.realm.toString() === realmAddress.toString()
  );

  const proposalPubkeys: PublicKey[] = filteredMetadatas.map(
    (metadata) => metadata.account.proposal
  );
  const chunkedProposalPubkeys = chunk(proposalPubkeys, 100);
  for (const chunkedProposals of chunkedProposalPubkeys) {
    const rawProposals = await RPC_CONNECTION.getMultipleAccountsInfoAndContext(
      chunkedProposals
    );
    for (const [index, rawProposal] of rawProposals.value.entries()) {
      if (!rawProposal) {
        continue;
      }
      const metadataAccount = filteredMetadatas[index].account;
      const deserializedProposal = deserializeBorsh(
        GOVERNANCE_SCHEMA,
        Proposal,
        rawProposal?.data
      );
      const proposalType = mapProposalTypeFromProgram(
        Object.keys(
          metadataAccount.proposalType
        )[0] as ProposalTypeProgramString
      );
      basicProposalsData.push({
        proposalAccount: {
          account: deserializedProposal,
          owner: splGovernanceProgram,
          pubkey: proposalPubkeys[index],
        },
        proposalMetadata: {
          currentFundraiseIndex: metadataAccount.currentFundraiseIndex,
          description: metadataAccount.description,
          discussionLink: metadataAccount.discussionLink,
          governance: metadataAccount.governance,
          totalFinanacialPower: metadataAccount.totalFinancialPower.toNumber(),
          treasury: metadataAccount.treasury,
          proposalMetadata: filteredMetadatas[index].publicKey,
          maxVoterWeight: metadataAccount.maxVoterWeight
            ? metadataAccount.maxVoterWeight.toNumber()
            : undefined,
          proposalIndex: metadataAccount.proposalIndex,
          proposalStatus: Object.keys(
            metadataAccount.proposalStatus
          )[0] as ProposalStatus,
          proposalType,
          voteThreshold: metadataAccount.approvalQuorumPercentage,
          proposalAction: mapProposalAction(proposalType),
          createdAt: metadataAccount.createdAt.toNumber(),
        },
      });
    }
  }

  return basicProposalsData.sort((a, b) =>
    a.proposalAccount.account.draftAt > b.proposalAccount.account.draftAt
      ? -1
      : 1
  );
}

/**
 * Get club's proposal by address
 * @param proposal {ProgramAccount<Proposal>} Proposal account from spl governance
 * @param realmName {string} Club name
 * @param wallet {AnchorWallet} Member's wallet
 * @param proposalGovernance? {Governance | undefined} Governance account for proposal
 * @param memberRights? {IMembersRights} Member's rights
 * @return {IProposalWithMetaData}
 */
export async function getProposalByAddress(
  proposal: IBasicProposalData,
  realmName: string,
  wallet: AnchorWallet,
  reservedRights: IReservedRights | undefined,
  denominatedCurrency: ITreasuryTokenAccInfo | undefined,
  proposalGovernance?: Governance | undefined,
  memberRights?: IMembersRights
): Promise<IProposalWithMetaData> {
  const governance =
    proposalGovernance ??
    (
      await getGovernance(
        RPC_CONNECTION,
        proposal.proposalAccount.account.governance
      )
    ).account;

  if (!governance) {
    throw new Error("Wrong governance address");
  }
  const clubProgram = programFactory();

  let metadata;

  const updatedProposalAccount = await getProposal(
    RPC_CONNECTION,
    proposal.proposalAccount.pubkey
  );

  const optionIndexBuffer = Buffer.alloc(1);
  optionIndexBuffer.writeUInt8(0, 0);
  const instructionIndexBuffer = Buffer.alloc(2);
  instructionIndexBuffer.writeInt16LE(0, 0);

  const [instructionAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      proposal.proposalAccount.pubkey.toBuffer(),
      optionIndexBuffer,
      instructionIndexBuffer,
    ],
    splGovernanceProgram
  );

  const proposalTransactionRaw = await RPC_CONNECTION.getAccountInfo(
    instructionAddress
  );
  let proposalTransaction: ProgramAccount<ProposalTransaction> | undefined;
  if (proposalTransactionRaw && proposalTransactionRaw.data) {
    proposalTransaction = {
      account: deserializeBorsh(
        GOVERNANCE_SCHEMA,
        ProposalTransaction,
        proposalTransactionRaw.data
      ),
      owner: splGovernanceProgram,
      pubkey: instructionAddress,
    };
  }

  const proposalMetadataFromProgram =
    await clubProgram.account.proposalMetadata.fetch(
      proposal.proposalMetadata.proposalMetadata
    );

  if (!proposalTransaction) {
    metadata = {
      proposalAction: ProposalAction.Discussion,
    };
  } else {
    metadata = await getProposalMetaData(
      proposal,
      realmName,
      proposalTransaction.account,
      wallet,
      reservedRights,
      denominatedCurrency,
      memberRights
    );
  }

  return {
    proposalBasicData: {
      proposalAccount: updatedProposalAccount,
      proposalMetadata: {
        ...proposal.proposalMetadata,
        proposalStatus: Object.keys(
          proposalMetadataFromProgram.proposalStatus
        )[0] as ProposalStatus,
      },
    },
    metadata,
    governanceAccount: governance,
    proposalTransactionInfo: proposalTransaction,
  };
}

/**
 * Vote for specified proposal
 * @param proposal {IProposalWithMetaData} Proposal with metadata
 * @param realmName {string} Club name
 * @param wallet {AnchorWallet} Member's wallet
 * @param clubTreasury {PublicKey} Club treasury
 * @param voteType {number} Vote type (0 - yes, 1 - no)
 * @param clubType {ClubType} Club type
 * @param mint {PublicKey} Communtiy mint
 * @param cancelProposal? {boolean} Flag that shows if founder canceling proposal that is not created by him
 */
export const voteToProposal = async (
  proposal: IProposalWithMetaData,
  realmName: string,
  wallet: AnchorWallet,
  clubTreasury: PublicKey,
  voteType: number,
  clubType: ClubType,
  mint: PublicKey,
  cancelProposal?: boolean,
  cancellationDto?: ICancellationReasonDto
) => {
  const clubProgram = programFactory();
  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      realmAddress.toBuffer(),
      mint.toBuffer(),
      wallet.publicKey.toBuffer(),
    ],
    splGovernanceProgram
  );

  const [maxVoterWeightPda] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
      maxVoterWeightSeed,
    ],
    clubProgram.programId
  );

  try {
    const recentBlockhash = await RPC_CONNECTION.getLatestBlockhash();
    const castVoteTransaction = new Transaction({
      recentBlockhash: recentBlockhash.blockhash,
      feePayer: wallet.publicKey,
    });
    const instructions: TransactionInstruction[] = [];

    const { updateVW, voterWeightAddress } = await updateVoterWeight(
      realmAddress,
      wallet,
      clubTreasury,
      proposal.proposalBasicData.proposalMetadata.currentFundraiseIndex,
      cancelProposal ? ClubAction.CancelProposal : ClubAction.CastVote,
      clubType,
      proposal.proposalBasicData.proposalAccount.pubkey,
      false
    );

    instructions.push(updateVW);

    await withCastVote(
      instructions,
      splGovernanceProgram,
      SPL_PROGRAM_VERSION,
      realmAddress,
      proposal.proposalBasicData.proposalAccount.account.governance,
      proposal.proposalBasicData.proposalAccount.pubkey,
      proposal.proposalBasicData.proposalAccount.account.tokenOwnerRecord,
      tokenOwnerRecord,
      wallet.publicKey, //Governance authority - vote authority
      mint,
      Vote.fromYesNoVote(voteType),
      wallet.publicKey,
      voterWeightAddress,
      maxVoterWeightPda
    );

    for (const instr of instructions) {
      castVoteTransaction.add(instr);
    }

    const signedTx = await wallet.signTransaction(castVoteTransaction);
    if (cancellationDto) {
      const serializedTx = signedTx.serialize();
      cancellationDto.signedTransaction = encode(serializedTx);
      await saveCancellationReason(cancellationDto);
    }

    await sendTransaction({
      transaction: signedTx,
      connection: RPC_CONNECTION,
      wallet: wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * Vote for specified proposal with NFTs
 * @param proposal {IProposalWithMetaData} Proposal with metadata
 * @param realmName {string} Club name
 * @param wallet {AnchorWallet} Member's wallet
 * @param clubTreasury {PublicKey} Club treasury
 * @param voteType {number} Vote type (0 - yes, 1 - no)
 * @param mint {PublicKey} Communtiy mint
 * @param nfts {IDetailNftData[]} Array of nfts for voting
 * @param clubType {ClubType} Club type
 */
export const withCastNftVote = async (
  proposal: IProposalWithMetaData,
  realmName: string,
  wallet: AnchorWallet,
  clubTreasury: PublicKey,
  voteType: number,
  mint: PublicKey,
  nfts: IDetailNftData[],
  clubType: ClubType
) => {
  const clubProgram = programFactory();

  const nftRemainingAccounts: AccountMeta[] = [];

  const nftTransactionInstructions: TransactionInstruction[] = [];
  const updateVwAndCastVoteInstructions: TransactionInstruction[] = [];

  const instuctionsSet: IInstructionSet[] = [];

  try {
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        realmAddress.toBuffer(),
        mint.toBuffer(),
        wallet.publicKey.toBuffer(),
      ],
      splGovernanceProgram
    );
    const [maxVoterWeightPda] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        maxVoterWeightSeed,
      ],
      clubProgram.programId
    );
    const [memberPDA] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    //Adding remaining accounts for all nft-s that are selected for voting
    for (const nft of nfts) {
      const [nftVoteRecordInfoAddress] = await PublicKey.findProgramAddress(
        [
          nftVoteRecordSeed,
          proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
          nft.mint.toBuffer(),
        ],
        clubProgram.programId
      );
      nftRemainingAccounts.push(
        {
          pubkey: nft.tokenAccount!,
          isWritable: false,
          isSigner: false,
        },
        {
          pubkey: nft.metadataAddress!,
          isWritable: false,
          isSigner: false,
        },
        {
          pubkey: nftVoteRecordInfoAddress,
          isWritable: true,
          isSigner: false,
        }
      );
    }

    //Find voterWeightAddress for CastNFt instruction
    const [voterWeightAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        voterWeightSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    //Chunk nft remaining accounts
    const chunkedNftRemainingAccounts = chunk(nftRemainingAccounts, 12);

    //Create instructions for cast nft vote
    for (const nftReaminingAcc of chunkedNftRemainingAccounts) {
      const castNftVoteIx = clubProgram.instruction.castNftVote({
        accounts: {
          realm: realmAddress,
          clubData,
          voterWeightRecord: voterWeightAddress,
          memberData: memberPDA,
          payer: wallet.publicKey,
          proposal: proposal.proposalBasicData.proposalAccount.pubkey,
          systemProgram: web3.SystemProgram.programId,
        },
        remainingAccounts: nftReaminingAcc,
      });
      nftTransactionInstructions.push(castNftVoteIx);
    }

    const { updateVW } = await updateVoterWeight(
      realmAddress,
      wallet,
      clubTreasury,
      proposal.proposalBasicData.proposalMetadata.currentFundraiseIndex,
      ClubAction.CastVote,
      clubType,
      proposal.proposalBasicData.proposalAccount.pubkey
    );
    updateVwAndCastVoteInstructions.push(updateVW);

    //with cast vote instruction
    await withCastVote(
      updateVwAndCastVoteInstructions,
      splGovernanceProgram,
      SPL_PROGRAM_VERSION,
      realmAddress,
      proposal.proposalBasicData.proposalAccount.account.governance,
      proposal.proposalBasicData.proposalAccount.pubkey,
      proposal.proposalBasicData.proposalAccount.account.tokenOwnerRecord,
      tokenOwnerRecord,
      wallet.publicKey,
      mint,
      Vote.fromYesNoVote(voteType),
      wallet.publicKey,
      voterWeightAddress,
      maxVoterWeightPda
    );
    //concat all instructions into one array
    const chunkedCastNftVoteIx = chunk(nftTransactionInstructions, 2);
    chunkedCastNftVoteIx.forEach((ix) => {
      instuctionsSet.push({
        instructions: ix,
        description: "Preparing NFTs for voting",
      });
    });
    instuctionsSet.push({
      instructions: updateVwAndCastVoteInstructions,
      description: "Cast votes",
    });

    await sendTransactions(
      RPC_CONNECTION,
      wallet,
      instuctionsSet,
      SequenceType.Sequential
    );
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * Execute Vault/Treasury related proposal
 * @param proposalWithMetaData {IProposalWithMetaData} Proposal with metadata
 * @param membersWallet {AnchorWallet} Member's wallet
 * @param realmName {string} Club name
 * @param treasury {PublicKey} Club's treasury
 * @return {Promise<void>}
 */
export const executeVaultTreasuryRelatedProposal = async (
  proposalWithMetaData: IProposalWithMetaData,
  membersWallet: AnchorWallet,
  realmName: string,
  treasury: PublicKey
): Promise<void> => {
  const clubProgram = programFactory();
  const escrowProgram = escrowProgramFactory();
  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );
  const allInstructions: IInstructionSet[] = [];

  //TODO@all:use this when we implement marketplace
  const isMarketplaceProposal = false;

  const [escrowedWantedTokenAccount] = await PublicKey.findProgramAddress(
    [
      escrowSeed,
      proposalWithMetaData.metadata.offer.toBuffer(),
      acceptOfferSeed,
    ],
    ESCRROW_PROGRAM
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );
  const [proposalMetadata] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      proposalWithMetaData.proposalBasicData.proposalAccount.pubkey.toBuffer(),
      proposalMetadataSeed,
    ],
    clubProgram.programId
  );

  const executeRemainingAccounts: AccountMeta[] = [];

  try {
    const executeProposalIxs: TransactionInstruction[] = [];
    if (
      proposalWithMetaData.proposalBasicData.proposalMetadata.proposalAction ===
      ProposalAction.BuyNft
    ) {
      const transferProfitIxs = await prepareTransferProfitIx(
        treasury,
        proposalWithMetaData.proposalBasicData.proposalAccount.pubkey,
        proposalWithMetaData.metadata.offeredTokenMint,
        membersWallet,
        proposalWithMetaData.metadata.offeredAmount,
        clubData
      );

      if (transferProfitIxs.transfeProfitIx) {
        if (transferProfitIxs.allInstructions.length > 0) {
          allInstructions.push(transferProfitIxs.allInstructions[0]);
        }
        executeProposalIxs.push(transferProfitIxs.transfeProfitIx);
      }
    }
    const executeProposalInstruction =
      clubProgram.instruction.executeTransaction(
        CHAIN_ID,
        proposalWithMetaData.metadata.proposalAction,
        TREASURY_INDEX + 1,
        {
          accounts: {
            clubData,
            governance:
              proposalWithMetaData.proposalBasicData.proposalAccount.account
                .governance,
            proposalMetadata,
            realm: realmAddress,
            proposal:
              proposalWithMetaData.proposalBasicData.proposalAccount.pubkey,
            proposalTransaction:
              proposalWithMetaData.proposalTransactionInfo!.pubkey,
            splGovernanceProgram,
            payer: membersWallet.publicKey,
            offer: proposalWithMetaData.metadata.offer,
            makerOfferedTokens:
              proposalWithMetaData.metadata.makerOfferedTokenAccount!,
            escrowedWantedTokenAccount,
            maker: proposalWithMetaData.metadata.maker,
            escrowedOfferedTokens:
              proposalWithMetaData.metadata.escrowedOfferedTokensAccountPda!,
            offeredTokensMint: proposalWithMetaData.metadata.offeredTokenMint,
            wantedTokensMint: proposalWithMetaData.metadata.wantedTokenMint,
            escrowProgram: ESCRROW_PROGRAM,
            treasury: treasury,
            tokenProgram: TOKEN_PROGRAM_ID,
            systemProgram: web3.SystemProgram.programId,
            rent: web3.SYSVAR_RENT_PUBKEY,
          },
          remainingAccounts: executeRemainingAccounts,
        }
      );

    executeProposalIxs.push(executeProposalInstruction);

    allInstructions.push({
      instructions: executeProposalIxs,
      description: "Execute proposal",
    });
    await sendTransactions(RPC_CONNECTION, membersWallet, allInstructions);
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * Execute withdrawal proposal
 * @param proposal {IProposalWithMetaData} Proposal to be executed
 * @param realmName {string} Realm name
 * @param treasury {PublicKey} Club's treasury
 * @param wallet {AnchorWallet} Member's wallet
 * @param withdrawalGovernance? {PublicKey} Withdrawal governance if exists
 */
export async function executeWithdrawalProposal(
  proposal: IProposalWithMetaData,
  realmName: string,
  treasury: PublicKey,
  wallet: AnchorWallet,
  withdrawalGovernance?: PublicKey
) {
  try {
    const clubProgram = programFactory();
    const escrowProgram = escrowProgramFactory();
    const allInstructions: IInstructionSet[] = [];

    const optionIndex = 0;
    const transactionIndex = 0;
    const optionIndexBuffer = Buffer.alloc(1);
    optionIndexBuffer.writeUInt8(optionIndex, 0);
    const instructionIndexBuffer = Buffer.alloc(2);
    instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [proposalMetadata] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        proposalMetadataSeed,
      ],
      clubProgram.programId
    );

    const [realmConfigAddress] = await PublicKey.findProgramAddress(
      [realmConfigSeed, realmAddress.toBuffer()],
      splGovernanceProgram
    );
    //TODO@milica:get withdrawal gov after program update
    const [governanceAddress] = await PublicKey.findProgramAddress(
      [accountGovernanceSeed, realmAddress.toBuffer(), treasury.toBuffer()],
      splGovernanceProgram
    );
    const [transferProfitAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        transferProfitSeed,
      ],
      clubProgram.programId
    );
    const [instructionAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        optionIndexBuffer,
        instructionIndexBuffer,
      ],
      splGovernanceProgram
    );

    const [profit] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.toBuffer(), profitSeed],
      clubProgram.programId
    );

    const [memberPDA] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    const [treasuryData] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
      clubProgram.programId
    );

    const profitTokenAccounts =
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(profit, {
        mint: proposal.metadata.withdrawalMint,
      });

    //Note: If profit account for specific treasury token doesnt exists, we need to first create it
    //and after that transfer profit (even if profit balance is zero) because execute withdrawal proposal ix
    //expected that transferProfitAddress is initialized
    //This will be optimized both on chain and front

    let profitTokenAccount;
    if (profitTokenAccounts.value.length === 0) {
      const { tokenAccount, createAccountIx, createInitAccountInstruction } =
        await createTokenAccountOwnedBySpecifiedAccount(
          wallet.publicKey,
          profit,
          proposal.metadata.withdrawalMint
        );
      allInstructions.push({
        instructions: [createAccountIx, createInitAccountInstruction],
        description: "Create Financial account",

        partialSigner: tokenAccount,
      });
      profitTokenAccount = tokenAccount.publicKey;
    } else {
      profitTokenAccount = profitTokenAccounts.value[0].pubkey;
    }

    const divided = divide(
      proposal.metadata.withdrawalAmount,
      getMintDecimalsDivider(proposal.metadata.mintDecimals)
    );

    const transferProfitIx = clubProgram.instruction.transferProfit(
      new BN(divided).mul(new BN(proposal.metadata.mintDecimals)),
      {
        accounts: {
          clubData,
          payer: wallet.publicKey,
          treasury: treasury,
          treasuryToken: proposal.metadata.treasuryToken,
          treasuryData: treasuryData,
          profit,
          transferProfit: transferProfitAddress,
          proposal: proposal.proposalBasicData.proposalAccount.pubkey,
          profitToken: profitTokenAccount,
          memberData: memberPDA,
          systemProgram: web3.SystemProgram.programId,
          tokenProgram: TOKEN_PROGRAM_ID,
        },
      }
    );

    //TODO@all:check withdrawal mint later
    const executeWithdrawalProposalIx =
      clubProgram.instruction.executeWithdrawalTransaction({
        accounts: {
          clubData,
          governance: withdrawalGovernance ?? governanceAddress,
          realm: realmAddress,
          realmConfig: realmConfigAddress,
          proposal: proposal.proposalBasicData.proposalAccount.pubkey,
          proposalMetadata,
          splGovernanceProgram,
          payer: wallet.publicKey,
          treasury: treasury,
          treasuryData: treasuryData,
          treasuryToken: proposal.metadata.treasuryToken,
          withdrawal: proposal.metadata.withdrawal,
          withdrawalData: proposal.metadata.withdrawalData,
          withdrawalMint: proposal.metadata.withdrawalMint,
          escrowProgram: escrowProgram.programId,
          proposalTransaction: instructionAddress,
          systemProgram: web3.SystemProgram.programId,
          transferProfitData: transferProfitAddress,
          rent: SYSVAR_RENT_PUBKEY,
          tokenProgram: TOKEN_PROGRAM_ID,
        },
      });

    allInstructions.push({
      description: "Execute withdrawal proposals",
      instructions: [transferProfitIx, executeWithdrawalProposalIx],
    });
    await sendTransactions(RPC_CONNECTION, wallet, allInstructions);
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Claim tokens after execution of withdrawal proposal
 * @param wallet {AnchorWallet} Member's wallet
 * @param withdrawal {PublicKey} Withdrawal address
 * @param withdrawalData {PublicKey} Withdrawal data address
 * @param realmName {string} Realm name
 * @param clubTreasury {IClubTreasury} Club's treasury
 */
export async function claimWithdrawalAmount(
  wallet: AnchorWallet,
  withdrawal: PublicKey,
  withdrawalData: PublicKey,
  realmName: string,
  clubTreasury?: IClubTreasury
) {
  try {
    const escrowProgram = escrowProgramFactory();
    const clubProgram = programFactory();

    const withdrawalDataAccount =
      await escrowProgram.account.withdrawalData.fetch(withdrawalData);

    const [realmAddress] = await web3.PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [memberAddress, memberBump] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    for (let i = withdrawalDataAccount.fundraiseNumber; i > 0; i--) {
      let countBuff = Buffer.alloc(4);
      countBuff.writeInt32LE(i, 0);
      let treasuryPda = clubTreasury?.treasury;
      if (!clubTreasury) {
        const [treasuryAddress] = await PublicKey.findProgramAddress(
          [
            unqClubSeed,
            clubData.toBuffer(),
            treasurySeed,
            getTreasuryCount(TREASURY_INDEX),
          ],
          clubProgram.programId
        );
        treasuryPda = treasuryAddress;
      }
    }
    const [withdrawalRecord] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        withdrawal.toBuffer(),
        withdrawalRecordSeed,
        wallet.publicKey.toBuffer(),
      ],
      escrowProgram.programId
    );

    const blockhash = (await RPC_CONNECTION.getLatestBlockhash()).blockhash;
    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash: blockhash,
    });

    const membersTAs = await RPC_CONNECTION.getParsedTokenAccountsByOwner(
      wallet.publicKey,
      {
        mint: withdrawalDataAccount.withdrawalMint,
      }
    );

    let membersTA: PublicKey;

    if (membersTAs.value.length > 0) {
      membersTA = membersTAs.value[0].pubkey;
    } else {
      membersTA = await Token.getAssociatedTokenAddress(
        ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
        TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
        withdrawalDataAccount.withdrawalMint, // mint
        wallet.publicKey, // owner
        false
      );
      tx.add(
        Token.createAssociatedTokenAccountInstruction(
          ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
          TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
          withdrawalDataAccount.withdrawalMint, // mint
          membersTA, // ata
          wallet.publicKey, // owner of token account
          wallet.publicKey // fee payer
        )
      );
    }

    const { treasuryData } = await getFinancialOfferPDAs(realmName, wallet);

    const [financialRecord] = await getFinancialRecordPDA(
      treasuryData,
      wallet.publicKey
    );

    const requestComputeUnitsTx = ComputeBudgetProgram.setComputeUnitLimit({
      units: 3000000,
    });
    tx.add(requestComputeUnitsTx);

    const claimIx = await escrowProgram.methods
      .withdraw()
      .accounts({
        payer: wallet.publicKey,
        financialRecord: financialRecord,
        memberData: memberAddress,
        withdrawal: withdrawal,
        withdrawalData: withdrawalData,
        withdrawalRecord,
        systemProgram: web3.SystemProgram.programId,
        memberTokens: membersTA,
        tokenProgram: TOKEN_PROGRAM_ID,
        treasuryData: treasuryData,
      })
      .instruction();

    tx.add(claimIx);
    const signedTx = await wallet.signTransaction(tx);

    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet: wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Create discussion proposal
 * @param realmName {string} Club name
 * @param payer {AnchorWallet} User's wallet
 * @param name {string} Proposal name
 * @param description {string} Proposal description
 * @param clubTreasury {IClubTreasury} Club treasury
 */
export async function createDiscussionProposal(
  realmName: string,
  payer: AnchorWallet,
  name: string,
  description: string,
  discussionLink: string,
  clubTreasury: PublicKey,
  clubType: ClubType,
  mint: PublicKey,
  fundraiseCount: number
) {
  const clubProgram = programFactory();
  let countBuffer = Buffer.alloc(4);
  countBuffer.writeInt32LE(fundraiseCount, 0);
  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [clubData, cdBump] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [treasuryAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );

  const [governanceAddress] = await PublicKey.findProgramAddress(
    [
      accountGovernanceSeed,
      realmAddress.toBuffer(),
      treasuryAddress.toBuffer(),
    ],
    splGovernanceProgram
  );

  const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      realmAddress.toBuffer(),
      mint.toBuffer(),
      payer.publicKey.toBuffer(),
    ],
    splGovernanceProgram
  );

  const instructions: TransactionInstruction[] = [];

  const accountGovernance = await getGovernance(
    RPC_CONNECTION,
    governanceAddress
  );
  let proposalIndexBuffer = Buffer.alloc(4);
  proposalIndexBuffer.writeInt32LE(accountGovernance.account.proposalCount, 0);

  let [proposalAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      accountGovernance.pubkey.toBuffer(),
      mint.toBuffer(),
      proposalIndexBuffer,
    ],
    splGovernanceProgram
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      payer.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [realmPda] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [proposalMetadata] = await PublicKey.findProgramAddress(
    [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
    clubProgram.programId
  );

  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, clubTreasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  try {
    const createProposalMetadataInstruction =
      clubProgram.instruction.createProposalMetadata(
        ProposalTypeForProgram.Discussion,
        null,
        null,
        null,
        {
          accounts: {
            realm: realmAddress,
            clubData,
            proposal: proposalAddress,
            proposalMetadata,
            memberData: memberPDA,
            governance: governanceAddress,
            treasuryData,
            payer: payer.publicKey,
            systemProgram: web3.SystemProgram.programId,
          },
        }
      );

    const updateProposalDescriptionIx =
      clubProgram.instruction.updateProposalDescription(
        description,
        name,
        ["option YES"],
        discussionLink,
        {
          accounts: {
            proposal: proposalAddress,
            proposalMetadata,
            payer: payer.publicKey,
          },
        }
      );

    const { updateVW, voterWeightAddress } = await updateVoterWeight(
      realmAddress,
      payer,
      clubTreasury,
      fundraiseCount,
      ClubAction.CreateDiscussionProposal,
      clubType,
      proposalAddress
    );
    instructions.push(updateVW);

    const realm = await getRealm(RPC_CONNECTION, realmAddress);

    const createdProposal = await withCreateProposal(
      instructions,
      splGovernanceProgram,
      SPL_PROGRAM_VERSION,
      realmAddress,
      governanceAddress,
      tokenOwnerRecord,
      name,
      discussionLink ?? EMPTY_STRING,
      mint,
      payer.publicKey,
      accountGovernance.account.proposalCount,
      VoteType.SINGLE_CHOICE,
      ["option YES"],
      true,
      payer.publicKey,
      voterWeightAddress
    );

    //TODO@all: check if this is needed
    await withAddSignatory(
      instructions,
      splGovernanceProgram,
      SPL_PROGRAM_VERSION,
      createdProposal,
      tokenOwnerRecord,
      payer.publicKey,
      payer.publicKey,
      payer.publicKey
    );

    const signatoryRecordAddress = await getSignatoryRecordAddress(
      splGovernanceProgram,
      createdProposal,
      payer.publicKey
    );

    withSignOffProposal(
      instructions,
      splGovernanceProgram,
      SPL_PROGRAM_VERSION,
      realm.pubkey,
      governanceAddress,
      createdProposal,
      payer.publicKey,
      signatoryRecordAddress,
      undefined
    );

    const allInstructions: IInstructionSet[] = [];
    allInstructions.push({
      instructions: [createProposalMetadataInstruction],
      description: "Create proposal metadata",
    });

    allInstructions.push({
      instructions: [updateProposalDescriptionIx],
      description: "Add proposal description",
    });

    allInstructions.push({
      instructions: [...instructions],
      description: "Create proposal account, update voter weight for user",
    });
    await sendTransactions(
      RPC_CONNECTION,
      payer,
      allInstructions,
      SequenceType.Sequential
    );

    return createdProposal;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Cancel escrow
 * @param realmName
 * @param payer
 * @param proposalWithMetaData
 * @param clubTreasury
 */
export async function cancelEscrow(
  realmName: string,
  payer: AnchorWallet,
  proposalAction: ProposalAction,
  offer: PublicKey,
  makerOfferedTokenAccount: PublicKey,
  maker: PublicKey,
  proposal: PublicKey,
  treasury: PublicKey,
  escrowedOfferedTokensAccountPda: PublicKey,
  cancellationReasonDto?: ICancellationReasonDto
) {
  const clubProgram = programFactory();
  const escrowProgram = escrowProgramFactory();
  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [memberAddress, memberBump] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      payer.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [escrowWantedTokenAccount] = await PublicKey.findProgramAddress(
    [escrowSeed, offer.toBuffer(), acceptOfferSeed],
    escrowProgram.programId
  );

  try {
    const latestBlockhash = await RPC_CONNECTION.getLatestBlockhash();

    const transaction = new Transaction({
      recentBlockhash: latestBlockhash.blockhash,
      feePayer: payer.publicKey,
    });

    const cancelEscrowInstruction = clubProgram.instruction.cancelEscrow(
      CHAIN_ID,
      proposalAction,
      TREASURY_INDEX + 1,
      {
        accounts: {
          realm: realmAddress,
          payer: payer.publicKey,
          memberData: memberAddress,
          offer: offer,
          makerOfferedTokens: makerOfferedTokenAccount,
          maker: maker,
          escrowedWantedTokens: escrowWantedTokenAccount,
          escrowedOfferedTokens: escrowedOfferedTokensAccountPda,
          escrowProgram: ESCRROW_PROGRAM,
          treasury: treasury,
          clubData,
          proposal,
          systemProgram: web3.SystemProgram.programId,
          tokenProgram: TOKEN_PROGRAM_ID,
        },
      }
    );

    transaction.add(cancelEscrowInstruction);

    const signedTx = await payer.signTransaction(transaction);

    if (cancellationReasonDto) {
      const serializedTx = encode(signedTx.serialize());
      cancellationReasonDto.signedTransaction = serializedTx;
      await saveCancellationReason(cancellationReasonDto);
    }

    await sendTransaction({
      transaction: signedTx,
      connection: RPC_CONNECTION,
      wallet: payer,
    });
    createNotification(MESSAGE_TYPE.SUCCESS, "Proposal successfully canceled!");
  } catch (error: any) {
    throw error;
  }
}

/**
 * Cancel proposal in voting state
 * @param realmName
 * @param proposalWithMetaData
 * @param payer
 */
export async function cancelProposal(
  realmName: string,
  proposalWithMetaData: IProposalWithMetaData,
  payer: AnchorWallet,
  cancellationReasonDto?: ICancellationReasonDto
) {
  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const instructions: TransactionInstruction[] = [];

  try {
    withCancelProposal(
      instructions,
      splGovernanceProgram,
      SPL_PROGRAM_VERSION,
      realmAddress,
      proposalWithMetaData.proposalBasicData.proposalAccount.account.governance,
      proposalWithMetaData.proposalBasicData.proposalAccount.pubkey,
      proposalWithMetaData.proposalBasicData.proposalAccount.account
        .tokenOwnerRecord,
      payer.publicKey
    );

    const latestBlockhash = await RPC_CONNECTION.getLatestBlockhash();
    const cancelProposalTx = new Transaction({
      feePayer: payer.publicKey,
      recentBlockhash: latestBlockhash.blockhash,
    });

    for (let instr of instructions) {
      cancelProposalTx.add(instr);
    }
    const signedTx = await payer.signTransaction(cancelProposalTx);
    if (cancellationReasonDto) {
      cancellationReasonDto.signedTransaction = encode(signedTx.serialize());
      await saveCancellationReason(cancellationReasonDto);
    }
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet: payer,
    });
    createNotification(MESSAGE_TYPE.SUCCESS, "Proposal canceled successfully!");
    return signedTx.signature;
  } catch (error: any) {
    console.log(error);
    createNotification(
      MESSAGE_TYPE.ERROR,
      "Problem with cancelling proposal",
      error.message
    );
  }
}

//TODO@guta@milica: add proposalIndex as parametar
/**
 * Update voter weight record for club member
 * @param realmAddress {PublicKey} Club address
 * @param payer {AnchorWallet} User's wallet
 * @param clubTreasury {IClubTreasury} Club treasury
 * @param updateVoterWeightAction {VoterWeightAction} Voter weight action
 * @param proposalIndex {number} Proposal index
 * @param remainingAccounts? {AccountMeta[]} Remaining accounts meta data
 */
export async function updateVoterWeight(
  realmAddress: PublicKey,
  payer: AnchorWallet,
  treasury: PublicKey,
  fundraiseCount: number,
  updateVoterWeightAction: ClubAction,
  clubType: ClubType,
  proposalAddress: PublicKey,
  isSellPermissionFromZeroAndUserIsFounder?: boolean
) {
  const clubProgram = programFactory();

  const [proposalMetadata] = await PublicKey.findProgramAddress(
    [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
    clubProgram.programId
  );

  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      payer.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [voterWeightAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      voterWeightSeed,
      payer.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [maxVoterWeightRecord] = await PublicKey.findProgramAddress(
    [unqClubSeed, proposalAddress.toBuffer(), maxVoterWeightSeed],
    clubProgram.programId
  );
  if (
    (clubType === ClubType.TokenProportional ||
      clubType === ClubType.ProjectFundraise) &&
    updateVoterWeightAction === ClubAction.CastVote &&
    fundraiseCount
  ) {
    const [financialRecord] = await getFinancialRecordPDA(
      treasuryData,
      payer.publicKey
    );

    const financialAccount = await clubProgram.account.financialRecord.fetch(
      financialRecord
    );

    if (
      !financialAccount.depositRecords &&
      !isSellPermissionFromZeroAndUserIsFounder
    ) {
      throw new Error(
        "You did not deposit any tokens when this proposal was created"
      );
    }
  }

  const updateVW = clubProgram.instruction.updateVoterWeight(
    updateVoterWeightAction,
    {
      accounts: {
        realm: realmAddress,
        treasuryData: treasuryData,
        voterWeightRecord: voterWeightAddress,
        memberData: memberPDA,
        clubData,
        payer: payer.publicKey,
        maxVoterWeightRecord,
        proposalMetadata,
        proposal: proposalAddress,
        systemProgram: web3.SystemProgram.programId,
      },
    }
  );

  return { updateVW, voterWeightAddress };
}

/**
 * Get votes for user
 * @param wallet {AnchorWallet} User's wallet
 * @return {ProgramAccount<VoteRecord>[]}
 */
export const getMembersVoteResult = async (
  wallet: AnchorWallet
): Promise<ProgramAccount<VoteRecord>[]> => {
  try {
    return await getVoteRecordsByVoter(
      RPC_CONNECTION,
      splGovernanceProgram,
      wallet.publicKey
    );
  } catch (error) {
    console.log(error);
    return [];
  }
};

/**
 * Get proposal offer
 * @param clubName {string} Club name
 * @param proposalAddress {string} Proposal's address
 * @return {ProgramAccount<VoteRecord>[]}
 */
export const getProposalOffer = async (
  clubName: string,
  proposal: IBasicProposalData,
  proposalGovernance: Governance,
  wallet: AnchorWallet,
  denominatedCurrency: ITreasuryTokenAccInfo | undefined,
  memberRights?: IMembersRights,
  proposalWithMetaDataInfo?: IProposalWithMetaData
): Promise<IOffer> => {
  try {
    let proposalWithMetaData = proposalWithMetaDataInfo;
    const escrowProgram = escrowProgramFactory();
    const clubProgram = programFactory();

    const [offer] = await PublicKey.findProgramAddress(
      [offerSeed, proposal.proposalAccount.pubkey.toBuffer()],
      ESCRROW_PROGRAM
    );
    const [realmAddress] = await web3.PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(clubName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );
    const [treasuryPda, trBump] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasurySeed,
        getTreasuryCount(TREASURY_INDEX),
      ],
      clubProgram.programId
    );
    const [vaultAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasuryPda.toBuffer(),
        vaultSeed,
        Buffer.from(CHAIN_ID),
      ],
      clubProgram.programId
    );

    const offerData = await escrowProgram.account.offer.fetch(offer);

    let proposalType: ProposalAction;

    switch (offerData.maker.toString()) {
      case treasuryPda.toString():
        proposalType = ProposalAction.BuyNft;
        break;
      case vaultAddress.toString():
        proposalType = ProposalAction.SellNft;
        break;
      default:
        throw new Error("Can not read proposal type from offerData maker");
    }

    const [escrowedOfferedTokensAccountPda, escBump] =
      await PublicKey.findProgramAddress(
        [escrowSeed, offer.toBuffer()],
        ESCRROW_PROGRAM
      );

    if (!proposalWithMetaDataInfo) {
      proposalWithMetaData = await getProposalByAddress(
        proposal,
        clubName,
        wallet,
        undefined, //Note: we do not need withdrawal info here, so we are sending undefined as reserver rights
        denominatedCurrency,
        proposalGovernance
      );
    }

    const dedicatedTaker: any = offerData.dedicatedTaker
      ? offerData.dedicatedTaker
      : undefined;
    const status = OfferStatus[Object.keys(offerData.status)[0]];

    return {
      offerAddress: offer,
      maker: offerData.maker,
      makerWantedTokenAccount: offerData.escrowedWantedToken,
      offeredAmount: offerData.offeredAmount.toNumber(),
      wantedAmount: offerData.wantedAmount.toNumber(),
      wantedTokenMint: offerData.wantedTokenMint,
      type: proposalType,
      offeredTokenMint: proposalWithMetaData!.metadata.offeredTokenMint,
      makerOfferedTokenAccount:
        proposalWithMetaData!.metadata.makerOfferedTokenAccount!,
      escrowedOfferedTokensAccountPda: escrowedOfferedTokensAccountPda,
      proposalAddress: proposal.proposalAccount.pubkey,
      status,
      tokenLedgerMint: offerData.tokenLedgerMint,
      ledgerEntriesCount: offerData.ledgerEntriesCount,
      offerTreasury: offerData.treasury,
      dedicatedTaker: dedicatedTaker,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * Accept proposal offer
 * @param offer {IOffer} Offer
 * @param wallet {AnchorWallet} Wallet
 * @return {Promise<void>}
 */
export const acceptProposalsOffer = async (
  offer: IOffer,
  wallet: AnchorWallet,
  clubName: string,
  proposalGovernance: Governance,
  proposal: IBasicProposalData,
  proposalAction?: ProposalAction
) => {
  try {
    const escrowProgram = escrowProgramFactory(wallet);
    const clubProgram = programFactory(wallet);
    const allInstructions: IInstructionSet[] = [];
    const tokenAccounts = await RPC_CONNECTION.getParsedTokenAccountsByOwner(
      wallet.publicKey,
      { programId: TOKEN_PROGRAM_ID }
    );

    const [realmAddress] = await web3.PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(clubName)],
      splGovernanceProgram
    );

    const [clubData, cdBump] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [treasuryAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasurySeed,
        getTreasuryCount(TREASURY_INDEX),
      ],
      clubProgram.programId
    );

    const [escrowedWantedTokenAccount] = await PublicKey.findProgramAddress(
      [escrowSeed, offer.offerAddress.toBuffer(), acceptOfferSeed],
      ESCRROW_PROGRAM
    );

    let nftTokenAccount;

    if (offer.type === ProposalAction.BuyNft) {
      for (const tokenAcc of tokenAccounts.value) {
        if (
          tokenAcc.account.data.parsed.info.mint.toString() ===
            offer.wantedTokenMint.toString() &&
          tokenAcc.account.data.parsed.info.tokenAmount.amount === "1"
        )
          nftTokenAccount = tokenAcc.pubkey;
      }
      if (!nftTokenAccount) {
        throw new Error("Token account with specified Mint doesnt exist");
      }
    } else {
      const { createAccountIx, createInitAccountInstruction, tokenAccount } =
        await createTokenAccountOwnedBySpecifiedAccount(
          wallet.publicKey,
          wallet.publicKey,
          offer.offeredTokenMint
        );
      nftTokenAccount = tokenAccount.publicKey;
      allInstructions.push({
        instructions: [createAccountIx, createInitAccountInstruction],
        partialSigner: tokenAccount,
        description: "Create token account with offered token mint",
      });
    }

    const fungibleTAmint =
      offer.type === ProposalAction.BuyNft
        ? offer.offeredTokenMint
        : offer.wantedTokenMint;

    let fungibleTA = tokenAccounts.value.find(
      (item) => item.account.data.parsed.info.mint === fungibleTAmint.toString()
    )?.pubkey;

    if (!fungibleTA) {
      const { createAccountIx, createInitAccountInstruction, tokenAccount } =
        await createTokenAccountOwnedBySpecifiedAccount(
          wallet.publicKey,
          wallet.publicKey,
          fungibleTAmint
        );

      fungibleTA = tokenAccount.publicKey;

      allInstructions.push({
        instructions: [createAccountIx, createInitAccountInstruction],
        partialSigner: tokenAccount,
        description: "Create native mint token account",
      });
    }

    const acceptOfferInstruction = escrowProgram.instruction.accept({
      accounts: {
        offer: offer.offerAddress,
        proposal: offer.proposalAddress,
        escrowedOfferedToken: offer.escrowedOfferedTokensAccountPda,
        payer: wallet.publicKey,
        taker: wallet.publicKey,
        treasury: treasuryAddress,
        takerOfferedToken:
          offer.type === ProposalAction.BuyNft ? fungibleTA : nftTokenAccount,
        takerWantedToken:
          offer.type === ProposalAction.BuyNft ? nftTokenAccount : fungibleTA,
        wantedTokenMint: offer.wantedTokenMint,
        escrowedWantedToken: escrowedWantedTokenAccount,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: web3.SystemProgram.programId,
      },
    });

    const {
      createAccountIx,
      createInitAccountInstruction,
      tokenAccount,
      distributeInstruction,
      createNewProfitToken,
    } = await distributeProfitFromProposal(
      proposal!,
      wallet,
      treasuryAddress,
      clubName,
      proposalAction!,
      proposalGovernance,
      wallet,
      undefined, //TODO@Milica-monke: add denom curr
      false,
      escrowedWantedTokenAccount
    );

    if (createNewProfitToken) {
      allInstructions.push({
        instructions: [
          createNewProfitToken.createAccountIx,
          createNewProfitToken.createInitAccountInstruction,
        ],
        partialSigner: createNewProfitToken.tokenAccount,
        description: "Create profit token for selected mint",
      });
    }

    const distributeIxs: TransactionInstruction[] = [acceptOfferInstruction];
    if (
      proposal.proposalMetadata.proposalAction === ProposalAction.SellNft &&
      offer.wantedTokenMint.toString() === NATIVE_MINT.toString()
    ) {
      distributeIxs.push(...[createAccountIx, createInitAccountInstruction]);
    }
    distributeIxs.push(distributeInstruction);
    allInstructions.push({
      instructions: [...distributeIxs],
      partialSigner:
        proposal.proposalMetadata.proposalAction === ProposalAction.SellNft &&
        offer.wantedTokenMint.toString() === NATIVE_MINT.toString()
          ? tokenAccount
          : undefined,
      description: "Accept offer",
    });

    await sendTransactions(
      RPC_CONNECTION,
      wallet,
      allInstructions,
      SequenceType.Sequential
    );
    createNotification(MESSAGE_TYPE.SUCCESS, "Offer accepted");
  } catch (error: any) {
    throw error;
  }
};

/**
 * Get proposal offer status
 * @param offer {PublicKey} Offer
 * @return {Promise<OfferStatus}
 */
export const getProposalOfferStatus = async (
  offer: PublicKey
): Promise<OfferStatus> => {
  try {
    const escrowProgram = escrowProgramFactory();
    const offerData = await escrowProgram.account.offer.fetch(offer);
    console.log(offerData.status);

    return OfferStatus[Object.keys(offerData.status)[0]];
  } catch (error) {
    return OfferStatus.initialised;
  }
};

/**
 * Distribute profit after trading proposal
 * @param proposalWithMetaData {IProposalWithMetaData} Proposal
 * @param membersWallet {AnchorWallet} Member's wallet
 * @param treasury {IClubTreasury} Club's treasury
 * @param realmName {string} Realm name
 */
export const distributeProfitFromProposal = async (
  proposal: IBasicProposalData,
  membersWallet: AnchorWallet,
  treasury: PublicKey,
  realmName: string,
  proposalAction: ProposalAction,
  proposalGovernance: Governance,
  wallet: AnchorWallet,
  denominatedCurrency?: ITreasuryTokenAccInfo | undefined,
  shouldSignTransaction?: boolean,
  escrowedWantedTokeAccount?: PublicKey,
  proposalWithMetaData?: IProposalWithMetaData,
  offerData?: {
    wantedTokenMint: PublicKey;
    tokenLedgerMint: PublicKey;
    makerWantedTokenAccount: PublicKey;
    offerTreasury: PublicKey;
    offerAddress: PublicKey;
    offeredTokenMint: PublicKey;
  }
) => {
  try {
    const escrowProgram = escrowProgramFactory();
    const clubProgram = programFactory();

    let offer: any;
    if (!proposalWithMetaData && !offerData) {
      offer = await getProposalOffer(
        realmName,
        proposal,
        proposalGovernance,
        wallet,
        denominatedCurrency
      );
    } else {
      const tokenLedgerMint = offerData
        ? offerData.tokenLedgerMint
        : proposalWithMetaData!.metadata.proposalAction ===
          ProposalAction.BuyNft
        ? proposalWithMetaData!.metadata.wantedTokenMint
        : proposalWithMetaData!.metadata.offeredTokenMint;

      const [offerAddress] = await PublicKey.findProgramAddress(
        [offerSeed, proposal.proposalAccount.pubkey.toBuffer()],
        ESCRROW_PROGRAM
      );
      offer = offerData ?? {
        wantedTokenMint: proposalWithMetaData!.metadata.wantedTokenMint,
        tokenLedgerMint: tokenLedgerMint,
        offerTreasury: treasury,
        makerWantedTokenAccount:
          proposalWithMetaData!.metadata.makerWantedTokenAccount,
        offerAddress,
        offeredTokenMint: proposalWithMetaData?.metadata.offeredTokenMint,
      };
    }

    const [realmAddress] = await web3.PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [tokenLedger] = await PublicKey.findProgramAddress(
      [
        escrowSeed,
        treasury.toBuffer(),
        tokenLedgerSeed,
        offer.tokenLedgerMint.toBuffer(),
      ],
      escrowProgram.programId
    );
    let ledgerCount = 0;
    try {
      ledgerCount = (await escrowProgram.account.tokenLedger.fetch(tokenLedger))
        .ledgerEntriesCount;
    } catch (error) {
      console.log(error);
      ledgerCount = 0;
    }

    const ledgerBuffer = Buffer.alloc(4);
    if (proposalAction === ProposalAction.BuyNft) {
      ledgerBuffer.writeInt32LE(ledgerCount + 1, 0);
    } else {
      ledgerBuffer.writeInt32LE(ledgerCount, 0);
    }

    let [ledgerEntry] = await PublicKey.findProgramAddress(
      [escrowSeed, tokenLedger.toBuffer(), ledgerBuffer],
      escrowProgram.programId
    );

    if (proposalAction === ProposalAction.SellNft) {
      try {
        const ledgerEntryAcc = await escrowProgram.account.ledgerEntry.fetch(
          ledgerEntry
        );
        if (
          ledgerEntryAcc.sell &&
          ledgerEntryAcc.sell.createdAt.toNumber() > 0
        ) {
          throw new Error("Sell already exists");
        }
      } catch (error) {
        console.log(error);
        const ledgerBufferSell = Buffer.alloc(4);
        ledgerBufferSell.writeInt32LE(ledgerCount + 1, 0);
        const [ledgerEntrySell] = await PublicKey.findProgramAddress(
          [escrowSeed, tokenLedger.toBuffer(), ledgerBufferSell],
          escrowProgram.programId
        );
        ledgerEntry = ledgerEntrySell;
      }
    }

    const [profit] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.toBuffer(), profitSeed],
      clubProgram.programId
    );

    const [vaultPda] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasury.toBuffer(),
        vaultSeed,
        Buffer.from(CHAIN_ID),
      ],
      clubProgram.programId
    );

    let newProfitTokenAccount: PublicKey;
    const profitTokenAccounts =
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(profit, {
        mint:
          proposal.proposalMetadata.proposalAction === ProposalAction.BuyNft
            ? offer.offeredTokenMint
            : offer.wantedTokenMint,
      });

    let createNewProfitToken: ICreateTokenAcc | undefined = undefined;

    if (profitTokenAccounts.value.length === 0) {
      const { tokenAccount, createAccountIx, createInitAccountInstruction } =
        await createTokenAccountOwnedBySpecifiedAccount(
          membersWallet.publicKey,
          profit,
          proposal.proposalMetadata.proposalAction === ProposalAction.BuyNft
            ? offer.offeredTokenMint
            : offer.wantedTokenMint
        );
      newProfitTokenAccount = tokenAccount.publicKey;
      createNewProfitToken = {
        createAccountIx: createAccountIx,
        createInitAccountInstruction: createInitAccountInstruction,
        tokenAccount: tokenAccount,
      };
    } else {
      newProfitTokenAccount =
        profitTokenAccounts.value[profitTokenAccounts.value.length - 1].pubkey;
    }

    const makerWantedTokenAccount =
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(
        proposalAction === ProposalAction.BuyNft ? vaultPda : treasury,
        {
          mint: offer.wantedTokenMint,
        }
      );
    const distributeTx = new Transaction({
      feePayer: membersWallet.publicKey,
      recentBlockhash: (await RPC_CONNECTION.getLatestBlockhash()).blockhash,
    });

    const distributeInstruction = clubProgram.instruction.distribute(
      TREASURY_INDEX + 1,
      {
        accounts: {
          clubData,
          proposal: proposal.proposalAccount.pubkey,
          payer: membersWallet.publicKey,
          offer: offer.offerAddress,
          escrowProgram: escrowProgram.programId,
          treasury: offer.offerTreasury,
          tokenLedger,
          ledgerEntry,
          vault: vaultPda,
          profit,
          profitToken: newProfitTokenAccount,
          escrowedWantedToken:
            escrowedWantedTokeAccount ?? offer.makerWantedTokenAccount,
          makerWantedToken: makerWantedTokenAccount.value[0].pubkey,
          tokenProgram: TOKEN_PROGRAM_ID,
          systemProgram: web3.SystemProgram.programId,
          rent: web3.SYSVAR_RENT_PUBKEY,
        },
      }
    );
    distributeTx.add(distributeInstruction);
    const { tokenAccount, createAccountIx, createInitAccountInstruction } =
      await createTokenAccountOwnedBySpecifiedAccount(
        membersWallet.publicKey,
        profit,
        NATIVE_MINT
      );

    if (shouldSignTransaction) {
      distributeTx.add(createAccountIx);
      distributeTx.add(createInitAccountInstruction);
      distributeTx.partialSign(tokenAccount);
      const signedTx = await wallet.signTransaction(distributeTx);

      await sendTransaction({
        connection: RPC_CONNECTION,
        transaction: signedTx,
        wallet: wallet,
      });
    }
    return {
      createAccountIx,
      createInitAccountInstruction,
      tokenAccount,
      distributeInstruction,
      createNewProfitToken,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export async function createMagicEdenProposal(
  proposalName: string,
  proposalDescription: string,
  realmName: string,
  offeredAmount: number,
  wantedAmount: number,
  action: ProposalAction,
  treasury: IClubTreasury,
  userRole: string,
  mint: PublicKey,
  clubType: ClubType,
  nftMint: PublicKey,
  payer: AnchorWallet,
  nftTokenAccount?: PublicKey,
  sellPermissions?: ISellPermissionConfig[],
  discussionLink?: string
) {
  try {
    const { setCustomTitle } = useTransactionStore.getState();
    const clubProgram = programFactory();
    const escrowProgram = escrowProgramFactory();
    const allInstructions: IInstructionSet[] = [];
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [realmConfigPda] = await PublicKey.findProgramAddress(
      [realmConfigSeed, realmAddress.toBuffer()],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [nftMetadata] = await PublicKey.findProgramAddress(
      [metadataSeed, METADATA_PROGRAM_ID.toBuffer(), nftMint.toBuffer()],
      METADATA_PROGRAM_ID
    );

    const remainingAccounts: AccountMeta[] = [];

    const [defaultAccountGovernancePda] = await PublicKey.findProgramAddress(
      [
        accountGovernanceSeed,
        realmAddress.toBuffer(),
        treasury.treasury.toBuffer(),
      ],
      splGovernanceProgram
    );

    const { isSellPermissionFromZeroAndUserIsFounder } =
      await getAccountGovernanceForTradingProposal(
        sellPermissions,
        action === ProposalAction.BuyNft ? offeredAmount : wantedAmount,
        realmAddress,
        clubProgram,
        treasury,
        defaultAccountGovernancePda,
        userRole
      );

    remainingAccounts.push({
      isSigner: false,
      isWritable: true,
      pubkey: nftMetadata,
    });

    const optionIndex = 0;
    const transactionIndex = 0;
    const optionIndexBuffer = Buffer.alloc(1);
    optionIndexBuffer.writeUInt8(optionIndex, 0);

    let makerOfferedTokenAccount: PublicKey;
    let makerWantedTokenAccount: PublicKey;
    let wantedMint: PublicKey;
    let offeredMint: PublicKey;

    const instructionIndexBuffer = Buffer.alloc(2);
    instructionIndexBuffer.writeInt16LE(transactionIndex, 0);
    let accountGovernanceAddress: PublicKey | undefined;
    let isCreatingAndExecuting = false;
    let useDenyOption = true;
    if (action === ProposalAction.BuyNft) {
      const {
        accountGovernancePda,
        spcAccount,
        useDeny,
        isSellPermissionFromZeroAndUserIsFounder,
      } = await getAccountGovernanceForTradingProposal(
        sellPermissions,
        offeredAmount,
        realmAddress,
        clubProgram,
        treasury,
        defaultAccountGovernancePda,
        userRole
      );
      isCreatingAndExecuting = isSellPermissionFromZeroAndUserIsFounder;
      accountGovernanceAddress = accountGovernancePda;
      useDenyOption = useDeny;
    } else {
      const [accountGovernancePda] = await PublicKey.findProgramAddress(
        [
          accountGovernanceSeed,
          realmAddress.toBuffer(),
          treasury.treasury.toBuffer(),
        ],
        splGovernanceProgram
      );
      accountGovernanceAddress = accountGovernancePda;
    }
    const governance = await getGovernance(
      RPC_CONNECTION,
      accountGovernanceAddress
    );
    const [vaultAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasury.treasury.toBuffer(),
        vaultSeed,
        Buffer.from(CHAIN_ID),
      ],
      clubProgram.programId
    );
    const proposalIndexBuffer = Buffer.alloc(4);
    proposalIndexBuffer.writeInt32LE(governance!.account.proposalCount, 0);
    const [proposalAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        accountGovernanceAddress.toBuffer(),
        mint.toBuffer(),
        proposalIndexBuffer,
      ],
      splGovernanceProgram
    );
    const [proposalMetadata] = await PublicKey.findProgramAddress(
      [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
      clubProgram.programId
    );
    const [instructionAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        proposalAddress.toBuffer(),
        optionIndexBuffer,
        instructionIndexBuffer,
      ],
      splGovernanceProgram
    );
    const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        realmAddress.toBuffer(),
        mint.toBuffer(),
        payer.publicKey.toBuffer(),
      ],
      splGovernanceProgram
    );

    const [offer] = await PublicKey.findProgramAddress(
      [offerSeed, proposalAddress.toBuffer()],
      ESCRROW_PROGRAM
    );

    const [memberAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        payer.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    const { tokenAccount, createAccountIx, createInitAccountInstruction } =
      await createTokenAccountOwnedBySpecifiedAccount(
        payer.publicKey,
        treasury.treasury,
        NATIVE_MINT
      );

    allInstructions.push({
      instructions: [createAccountIx, createInitAccountInstruction],
      description: "Creating token account owned by treasury",
      partialSigner: tokenAccount,
    });

    if (action === ProposalAction.BuyNft) {
      makerOfferedTokenAccount = tokenAccount.publicKey;
      wantedMint = nftMint;
      const vaultTokenAccount = await createTokenAccountOwnedBySpecifiedAccount(
        payer.publicKey,
        vaultAddress,
        wantedMint
      );

      makerWantedTokenAccount = vaultTokenAccount.tokenAccount.publicKey;

      offeredMint = NATIVE_MINT;

      allInstructions.push({
        instructions: [
          vaultTokenAccount.createAccountIx,
          vaultTokenAccount.createInitAccountInstruction,
        ],
        partialSigner: vaultTokenAccount.tokenAccount,
        description: "Create token account owned by vault",
      });
    } else {
      makerOfferedTokenAccount = nftTokenAccount!;
      makerWantedTokenAccount = tokenAccount.publicKey;
      offeredMint = nftMint;
      wantedMint = NATIVE_MINT;
    }

    const { updateVW, voterWeightAddress } = await updateVoterWeight(
      realmAddress,
      payer,
      treasury.treasury,
      treasury.fundraiseCount,
      ClubAction.CreateMeProposal,
      clubType,
      proposalAddress,
      isSellPermissionFromZeroAndUserIsFounder
    );

    const txBuffer =
      action === ProposalAction.BuyNft
        ? await buyNowMagicEden(
            treasury.treasury.toString(),
            nftMint.toString()
          )
        : await sellMagicEden(
            await makeSellMagicEdenDto(
              wantedAmount,
              nftMint.toString(),
              nftTokenAccount!.toString(),
              vaultAddress
            )
          );

    const tx = Transaction.from(txBuffer);

    if (action === ProposalAction.BuyNft) {
      setCustomTitle("Buy NFT - Magic Eden");
    }

    const [magicEdenAccountsHolder] = await PublicKey.findProgramAddress(
      [unqClubSeed, offer.toBuffer(), magicEdenSeed],
      clubProgram.programId
    );

    const createMEProposaBuylIx =
      clubProgram.instruction.createMeBuyNowProposal(useDenyOption, {
        accounts: {
          governance: accountGovernanceAddress,
          realm: realmAddress,
          realmConfig: realmConfigPda,
          proposal: proposalAddress,
          proposalTransactionAddress: instructionAddress,
          tokenOwnerRecord,
          splGovernanceProgram,
          communityTokenMint: mint,
          payer: payer.publicKey,
          voterWeightRecord: voterWeightAddress,
          treasuryData: treasury.treasuryDataAddress,
          proposalMetadata,
          systemProgram: SystemProgram.programId,
          magicEdenData: magicEdenAccountsHolder,
          rent: SYSVAR_RENT_PUBKEY,
        },
        remainingAccounts,
      });

    const createMEProposaSelllIx = clubProgram.instruction.createMeSellProposal(
      useDenyOption,
      {
        accounts: {
          governance: accountGovernanceAddress,
          realm: realmAddress,
          realmConfig: realmConfigPda,
          proposal: proposalAddress,
          proposalTransactionAddress: instructionAddress,
          tokenOwnerRecord,
          splGovernanceProgram,
          payer: payer.publicKey,
          voterWeightRecord: voterWeightAddress,
          clubData,
          treasuryData: treasury.treasuryDataAddress,
          treasury: treasury.treasury,
          vault: vaultAddress,
          proposalMetadata,
          systemProgram: SystemProgram.programId,
          magicEdenData: magicEdenAccountsHolder,
          rent: SYSVAR_RENT_PUBKEY,
          communityTokenMint: mint,
        },
      }
    );

    const { initializeMeAccounts } =
      action === ProposalAction.BuyNft
        ? await getCreateProposalMetadataRAForBuyNow(
            tx,
            offer,
            magicEdenAccountsHolder
          )
        : await getCreateProposalMetadataRAForSell(
            tx,
            tokenAccount.publicKey,
            offer,
            magicEdenAccountsHolder
          );

    const createProposalMetadataInstruction =
      clubProgram.instruction.createProposalMetadata(
        action === ProposalAction.BuyNft
          ? ProposalTypeForProgram.BuyNowMagicEden
          : ProposalTypeForProgram.SellMagicEden,
        tx.instructions[0].data,
        action === ProposalAction.BuyNft ? tx.instructions[1].data : null,
        action === ProposalAction.BuyNft ? tx.instructions[2].data : null,
        {
          accounts: {
            clubData,
            proposal: proposalAddress,
            proposalMetadata,
            realm: realmAddress,
            memberData: memberAddress,
            governance: accountGovernanceAddress,
            treasuryData: treasury.treasuryDataAddress,
            payer: payer.publicKey,
            systemProgram: web3.SystemProgram.programId,
          },
          remainingAccounts: initializeMeAccounts,
        }
      );
    const updateProposalDescriptionIx =
      clubProgram.instruction.updateProposalDescription(
        proposalDescription,
        proposalName,
        ["option YES"],
        discussionLink ?? EMPTY_STRING,
        {
          accounts: {
            proposal: proposalAddress,
            proposalMetadata,
            payer: payer.publicKey,
          },
        }
      );
    allInstructions.push({
      description: "Create proposal metadata",
      instructions: [createProposalMetadataInstruction],
    });
    allInstructions.push({
      description: "Update proposal description",
      instructions: [updateProposalDescriptionIx],
    });
    allInstructions.push({
      description: "Create proposal instruction",
      instructions: [
        updateVW,
        action === ProposalAction.BuyNft
          ? createMEProposaBuylIx
          : createMEProposaSelllIx,
      ],
    });
    await sendTransactions(
      RPC_CONNECTION,
      payer,
      allInstructions,
      SequenceType.Sequential,
      isCreatingAndExecuting
    );
    return proposalAddress;
  } catch (error) {
    console.log(error);
    throw error;
  }
}
/**
 *
 * @param proposal Proposal to be executed
 * @param realmName Name of club
 * @param treasury Treasury address
 * @param wallet Payer wallet
 */
export async function executeMagicEdenBuyProposal(
  proposal: IProposalWithMetaData,
  realmName: string,
  treasury: PublicKey,
  wallet: AnchorWallet
) {
  try {
    const { setWarningDescription } = useTransactionStore.getState();
    const clubProgram = programFactory();
    const escrowProgram = escrowProgramFactory();
    const allInstructions: IInstructionSet[] = [];
    const optionIndex = 0;
    const transactionIndex = 0;
    const optionIndexBuffer = Buffer.alloc(1);
    optionIndexBuffer.writeUInt8(optionIndex, 0);

    setWarningDescription(
      "Due to technical implementation of Magic Eden’s API transaction may fail, and you need to retry."
    );
    const instructionIndexBuffer = Buffer.alloc(2);
    instructionIndexBuffer.writeInt16LE(transactionIndex, 0);
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );
    const [offer] = await PublicKey.findProgramAddress(
      [offerSeed, proposal.proposalBasicData.proposalAccount.pubkey.toBuffer()],
      ESCRROW_PROGRAM
    );
    const [escrowWantedTokenAccount] = await PublicKey.findProgramAddress(
      [escrowSeed, offer.toBuffer(), acceptOfferSeed],
      escrowProgram.programId
    );

    const [magicEdenAccountsHolder] = await PublicKey.findProgramAddress(
      [unqClubSeed, offer.toBuffer(), magicEdenSeed],
      clubProgram.programId
    );

    const [instructionAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        optionIndexBuffer,
        instructionIndexBuffer,
      ],
      splGovernanceProgram
    );

    const isNftOnEscrowWanted = (
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(
        escrowWantedTokenAccount,
        {
          mint: proposal.metadata.wantedTokenMint,
        }
      )
    ).value.find(
      (tokenAcc) =>
        +tokenAcc.account.data.parsed.info.tokenAmount.uiAmount === 1
    );
    if (!isNftOnEscrowWanted) {
      await compareBuyPrices(
        proposal.metadata.wantedTokenMint,
        proposal.metadata.offeredAmount
      );
      const txBuffer = await buyNowMagicEden(
        treasury.toString(),
        proposal.metadata.wantedTokenMint.toString()
      );

      const tx = Transaction.from(txBuffer);

      const [proposalMetadata] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
          proposalMetadataSeed,
        ],
        clubProgram.programId
      );
      if (
        proposal.metadata.offerStatus !==
        OfferStatus.pendingBuyNowExecutionOnMagicEden
      ) {
        const executeMagicEdenProposalIx =
          clubProgram.instruction.executeMeBuyNowTransaction({
            accounts: {
              governance:
                proposal.proposalBasicData.proposalAccount.account.governance,
              realm: realmAddress,
              proposal: proposal.proposalBasicData.proposalAccount.pubkey,
              proposalTransaction: instructionAddress,
              splGovernanceProgram,
              payer: wallet.publicKey,
              proposalMetadata,
              escrowProgram: escrowProgram.programId,
              clubData,
              treasury,
              offer: offer,
              unqEscrowTokens: escrowWantedTokenAccount,
              magicEdenEscrowedTokenMint: tx.instructions[2].keys[4].pubkey,
              tokenProgram: TOKEN_PROGRAM_ID,
              systemProgram: SystemProgram.programId,
              rent: SYSVAR_RENT_PUBKEY,
            },
          });

        allInstructions.push({
          description: "Executing proposal",
          instructions: [executeMagicEdenProposalIx],
        });
      }

      const [treasuryData] = await PublicKey.findProgramAddress(
        [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
        clubProgram.programId
      );

      const remAcc = getExecuteMeTransactionRemainingAccounts(tx);
      const meBuyNowAccounts = getExecuteMeTransactionAccounts(
        tx,
        wallet.publicKey,
        clubData,
        offer,
        magicEdenAccountsHolder,
        escrowWantedTokenAccount,
        proposalMetadata,
        treasuryData
      );
      const request = ComputeBudgetProgram.requestUnits({
        units: 1400000,
        additionalFee: 0,
      });
      const executeMageicEdenBuy = clubProgram.instruction.executeMeBuyNow(
        tx.instructions[0].data,
        tx.instructions[1].data,
        tx.instructions[2].data,
        {
          accounts: meBuyNowAccounts,
          remainingAccounts: remAcc,
        }
      );
      allInstructions.push({
        description: "Buying NFT from Magic Eden",
        instructions: [request, executeMageicEdenBuy],
      });
    }

    const distributeTx = await distributeProfitFromProposal(
      proposal.proposalBasicData,
      wallet,
      treasury,
      realmName,
      ProposalAction.BuyNft,
      proposal.governanceAccount,
      wallet,
      undefined, //TODO@milica-monke: add denom curr
      false,
      escrowWantedTokenAccount,
      proposal
    );
    allInstructions.push({
      description: "Transfering NFT to vault",
      instructions: [
        distributeTx.createAccountIx,
        distributeTx.createInitAccountInstruction,
        distributeTx.distributeInstruction,
      ],
      partialSigner: distributeTx.tokenAccount,
    });
    await sendTransactions(
      RPC_CONNECTION,
      wallet,
      allInstructions,
      SequenceType.Sequential
    );
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const executeMagicEdenSell = async (
  realmName: string,
  proposal: IProposalWithMetaData,
  treasury: PublicKey,
  wallet: AnchorWallet
) => {
  try {
    const clubProgram = programFactory();
    const escrowProgram = escrowProgramFactory();

    const allInstructions: IInstructionSet[] = [];
    const optionIndex = 0;
    const transactionIndex = 0;
    const optionIndexBuffer = Buffer.alloc(1);
    optionIndexBuffer.writeUInt8(optionIndex, 0);

    const instructionIndexBuffer = Buffer.alloc(2);
    instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

    const [realmPda] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmPda.toBuffer()],
      clubProgram.programId
    );

    const [vaultAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasury.toBuffer(),
        vaultSeed,
        Buffer.from(CHAIN_ID),
      ],
      clubProgram.programId
    );

    const [escrowWantedTokenAccount] = await PublicKey.findProgramAddress(
      [escrowSeed, proposal.metadata.offer.toBuffer(), acceptOfferSeed],
      escrowProgram.programId
    );

    const [instructionAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        optionIndexBuffer,
        instructionIndexBuffer,
      ],
      splGovernanceProgram
    );

    const vaultTokens = (
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(vaultAddress, {
        mint: proposal.metadata.offeredTokenMint,
      })
    ).value.find(
      (acc) => +acc.account.data.parsed.info.tokenAmount.amount === 1
    );

    if (!vaultTokens) {
      throw new Error("Can not find token account");
    }

    const sellMeDto = await makeSellMagicEdenDto(
      proposal.metadata.wantedAmount,
      proposal.metadata.offeredTokenMint.toString(),
      vaultTokens!.pubkey.toString(),
      vaultAddress
    );

    const [proposalMetadata] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        proposalMetadataSeed,
      ],
      clubProgram.programId
    );

    const [treasuryData] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
      clubProgram.programId
    );

    let txData = await sellMagicEden(sellMeDto);

    let tx = Transaction.from(txData);

    tx.instructions[0].keys.forEach((key) =>
      console.log(key.pubkey.toString())
    );

    const executeMeSellTransaction =
      clubProgram.instruction.executeMeSellTransaction({
        accounts: {
          governance:
            proposal.proposalBasicData.proposalAccount.account.governance,
          clubData,
          escrowProgram: escrowProgram.programId,
          makerWantedToken: proposal.metadata.makerWantedTokenAccount,
          nativeMint: NATIVE_MINT,
          offer: proposal.metadata.offer,
          payer: wallet.publicKey,
          treasury,
          proposalMetadata,
          unqEscrowTokens: escrowWantedTokenAccount,
          proposal: proposal.proposalBasicData.proposalAccount.pubkey,
          proposalTransaction: instructionAddress,
          splGovernanceProgram,
          realm: realmPda,
          vault: vaultAddress,
          rent: SYSVAR_RENT_PUBKEY,
          systemProgram: SystemProgram.programId,
          tokenProgram: TOKEN_PROGRAM_ID,
          magicEdenEscrowedTokenMint: proposal.metadata.offeredTokenMint,
        },
      });

    if (
      proposal.metadata.offerStatus !==
      OfferStatus.pendingSellExecutionOnMagicEden
    ) {
      allInstructions.push({
        description: "Executing proposal",
        instructions: [executeMeSellTransaction],
      });
    }

    const sellAccounts = getMagicEdenExecuteSell(
      tx,
      wallet.publicKey,
      clubData,
      treasury,
      vaultAddress,
      proposal.metadata.offer,
      vaultTokens.pubkey,
      treasuryData
    );
    const executeMeSell = clubProgram.instruction.executeMeSell(
      tx.instructions[0].data,
      {
        accounts: sellAccounts,
      }
    );
    const requestComputeUnitsTx = ComputeBudgetProgram.requestUnits({
      additionalFee: 0,
      units: 14000000,
    });
    allInstructions.push({
      description: "Listing NFT on Magic Eden",
      instructions: [requestComputeUnitsTx, executeMeSell],
    });
    await sendTransactions(RPC_CONNECTION, wallet, allInstructions);
  } catch (error) {
    throw error;
  }
};

export const createTransferProposal = async (
  realmName: string,
  clubType: ClubType,
  payer: AnchorWallet,
  name: string,
  description: string,
  discussionLink: string,
  amountToInvest: number,
  transferAddress: string,
  clubTreasury: IClubTreasury,
  mint: PublicKey,
  tokenAddress: PublicKey | undefined,
  decimals: number,
  fundraiseCount: number,
  communityTokenMint: PublicKey,
  nftMetadata?: PublicKey,
  membersNftTokenAccount?: PublicKey
) => {
  const clubProgram = programFactory();
  const escrowProgram = escrowProgramFactory();
  const remainingAccounts: AccountMeta[] = [];
  const allInstructions: IInstructionSet[] = [];

  const decimals_amount = Number(pow(10, decimals));
  const optionIndex = 0;
  const transactionIndex = 0;
  const optionIndexBuffer = Buffer.alloc(1);
  optionIndexBuffer.writeUInt8(optionIndex, 0);
  const instructionIndexBuffer = Buffer.alloc(2);
  instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );

  const [realmConfigPda] = await PublicKey.findProgramAddress(
    [realmConfigSeed, realmAddress.toBuffer()],
    splGovernanceProgram
  );

  const [clubData, cdBump] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );

  const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      realmAddress.toBuffer(),
      communityTokenMint.toBuffer(),
      payer.publicKey.toBuffer(),
    ],
    splGovernanceProgram
  );

  const [treasuryAddress] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );
  const [governanceAddress] = await PublicKey.findProgramAddress(
    [
      accountGovernanceSeed,
      realmAddress.toBuffer(),
      treasuryAddress.toBuffer(),
    ],
    splGovernanceProgram
  );
  const [treasuryData] = await PublicKey.findProgramAddress(
    [unqClubSeed, treasuryAddress.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );

  const treasuryDataAcc = await clubProgram.account.treasuryData.fetch(
    treasuryData
  );

  const denomMint =
    clubTreasury.denominatedTokenInfo?.mint.toString() ??
    NATIVE_MINT.toString();

  const {
    accountGovernancePda,
    useDeny,
    isSellPermissionFromZeroAndUserIsFounder,
  } = await getAccountGovernanceForTradingProposal(
    clubTreasury.sellPermissions,
    amountToInvest * decimals_amount,
    realmAddress,
    clubProgram,
    clubTreasury,
    treasuryDataAcc.transferGovernance ?? governanceAddress,
    undefined,
    mint.toString() === denomMint.toString()
  );

  const accountGovernance = await getGovernance(
    RPC_CONNECTION,
    accountGovernancePda
  );
  let proposalIndexBuffer = Buffer.alloc(4);
  proposalIndexBuffer.writeInt32LE(accountGovernance.account.proposalCount, 0);

  let [proposalAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      accountGovernance.pubkey.toBuffer(),
      communityTokenMint.toBuffer(),
      proposalIndexBuffer,
    ],
    splGovernanceProgram
  );

  const [instructionAddress] = await PublicKey.findProgramAddress(
    [
      governanceSeed,
      proposalAddress.toBuffer(),
      optionIndexBuffer,
      instructionIndexBuffer,
    ],
    splGovernanceProgram
  );

  const [offer] = await PublicKey.findProgramAddress(
    [offerSeed, proposalAddress.toBuffer()],
    ESCRROW_PROGRAM
  );

  const [memberPDA] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      unqClubMemberSeed,
      payer.publicKey.toBuffer(),
    ],
    clubProgram.programId
  );

  const [proposalMetadata] = await PublicKey.findProgramAddress(
    [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
    clubProgram.programId
  );

  let treasuryToken: PublicKey | undefined = undefined;

  //Note: when we implement logic when club has multiple token account with same mint
  //we will have to send specific token account as a parametar of the method
  //For now, we are using the first token account with selected mint.
  // Implemented

  if (tokenAddress) {
    treasuryToken = tokenAddress;
  } else {
    const treasuryTokenAccounts =
        await RPC_CONNECTION.getParsedTokenAccountsByOwner(treasuryAddress, {
          mint: mint,
        });

    if (treasuryTokenAccounts.value.length > 0) {
      treasuryToken = treasuryTokenAccounts.value[0].pubkey;
    } else {
      const { createAccountIx, createInitAccountInstruction, tokenAccount } =
        await createTokenAccountOwnedBySpecifiedAccount(
          payer.publicKey,
          treasuryAddress,
          mint
        );
      treasuryToken = tokenAccount.publicKey;
      allInstructions.push({
        instructions: [createAccountIx, createInitAccountInstruction],
        partialSigner: tokenAccount,
        description: "Create token account",
      });
    }
  }

  try {
    const createProposalMetadataInstruction =
      clubProgram.instruction.createProposalMetadata(
        ProposalTypeForProgram.TransferFunds,
        null,
        null,
        null,
        {
          accounts: {
            clubData,
            realm: realmAddress,
            proposal: proposalAddress,
            proposalMetadata,
            memberData: memberPDA,
            governance: accountGovernance.pubkey,
            treasuryData,
            payer: payer.publicKey,
            systemProgram: web3.SystemProgram.programId,
          },
        }
      );

    const updateProposalDescriptionIx =
      clubProgram.instruction.updateProposalDescription(
        description,
        name,
        ["option YES"],
        discussionLink,
        {
          accounts: {
            proposal: proposalAddress,
            proposalMetadata,
            payer: payer.publicKey,
          },
        }
      );

    const { updateVW, voterWeightAddress } = await updateVoterWeight(
      realmAddress,
      payer,
      clubTreasury.treasury,
      fundraiseCount,
      ClubAction.CreateTransferProposal,
      clubType,
      proposalAddress
    );

    let destination: PublicKey | undefined = undefined;
    let isTransferAddressATA = false;

    try {
      const transferAddressTABalance =
        await RPC_CONNECTION.getTokenAccountBalance(
          new PublicKey(transferAddress)
        );
      isTransferAddressATA = true;
    } catch (error) {
      console.log(error);
      isTransferAddressATA = false;
    }
    if (isTransferAddressATA) {
      const transferTAInfo: any = await RPC_CONNECTION.getParsedAccountInfo(
        new PublicKey(transferAddress)
      );
      if (
        transferTAInfo &&
        transferTAInfo.value?.data.parsed.info.mint.toString() ===
          mint.toString()
      ) {
        destination = new PublicKey(transferAddress);
      } else {
        throw new Error(`Provided token account's mint is not expected.`);
      }
    }
    if (destination === undefined) {
      if (mint.toString() === NATIVE_MINT.toString()) {
        destination = new PublicKey(transferAddress);
      } else {
        const destinationTA =
          await RPC_CONNECTION.getParsedTokenAccountsByOwner(
            new PublicKey(transferAddress),
            {
              mint: mint,
            }
          );

        if (destinationTA.value.length > 0) {
          destination = destinationTA.value[0].pubkey;
        } else {
          destination = await Token.getAssociatedTokenAddress(
            ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
            TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
            mint, // mint
            new PublicKey(transferAddress), // owner
            false
          );

          allInstructions.push({
            instructions: [
              Token.createAssociatedTokenAccountInstruction(
                ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
                TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
                mint, // mint
                destination, // ata
                new PublicKey(transferAddress), // owner of token account
                payer.publicKey // fee payer
              ),
            ],
            description: "Create ATA for transfer address",
          });
        }
      }
    }

    if (
      destination.toString() === treasuryToken.toString() ||
      (mint.toString() === NATIVE_MINT.toString() &&
        destination.toString() === treasuryAddress.toString())
    ) {
      throw new Error(
        "Destination addresss can not be the same as address from which tokens will be transfered"
      );
    }

    const transferAmount = new BN(amountToInvest).mul(new BN(decimals_amount));

    const createTransferProposal = await clubProgram.methods
      .createTransferProposal(useDeny, transferAmount)
      .accounts({
        clubData,
        communityTokenMint: communityTokenMint,
        destination: destination,
        treasuryToken: treasuryToken,
        governance: accountGovernance.pubkey,
        offer: offer,
        payer: payer.publicKey,
        proposal: proposalAddress,
        proposalMetadata: proposalMetadata,
        proposalTransactionAddress: instructionAddress,
        realm: realmAddress,
        realmConfig: realmConfigPda,
        rent: SYSVAR_RENT_PUBKEY,
        splGovernanceProgram: splGovernanceProgram,
        systemProgram: SystemProgram.programId,
        tokenOwnerRecord: tokenOwnerRecord,
        tokenProgram: TOKEN_PROGRAM_ID,
        treasury: treasuryAddress,
        treasuryData: treasuryData,
        voterWeightRecord: voterWeightAddress,
      })
      .instruction();

    allInstructions.push({
      instructions: [createProposalMetadataInstruction],
      description: "Create proposal metadata",
    });

    allInstructions.push({
      instructions: [updateProposalDescriptionIx],
      description: "Add proposal description",
    });

    allInstructions.push({
      instructions: [updateVW, createTransferProposal],
      description: "Create proposal account, update voter weight for user",
    });

    await sendTransactions(
      RPC_CONNECTION,
      payer,
      allInstructions,
      SequenceType.Sequential,
      true
    );
    return {
      proposalAddress,
      accountGovernanceIndex: accountGovernance.account.proposalCount,
      // TODO@milica-monke-merge: nftMetadata && membersNftTokenAccount is only for monke
      isZeroQuorum:
        accountGovernance.account.config.voteThresholdPercentage.value === 0 &&
        nftMetadata &&
        membersNftTokenAccount,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const executeTransferProposalTx = async (
  wallet: AnchorWallet,
  proposal: IProposalWithMetaData,
  realmName: string,
  clubTreasury: IClubTreasury
) => {
  try {
    const clubProgram = programFactory();
    const escrowProgram = escrowProgramFactory();

    const optionIndex = 0;
    const transactionIndex = 0;
    const optionIndexBuffer = Buffer.alloc(1);
    optionIndexBuffer.writeUInt8(optionIndex, 0);
    const instructionIndexBuffer = Buffer.alloc(2);
    instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [realmConfig] = await PublicKey.findProgramAddress(
      [realmConfigSeed, realmAddress.toBuffer()],
      splGovernanceProgram
    );

    const [treasuryData] = await PublicKey.findProgramAddress(
      [unqClubSeed, clubTreasury.treasury.toBuffer(), treasuryDataSeed],
      clubProgram.programId
    );

    const [proposalMetadata] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        proposalMetadataSeed,
      ],
      clubProgram.programId
    );

    const [instructionAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        optionIndexBuffer,
        instructionIndexBuffer,
      ],
      splGovernanceProgram
    );

    const [tokenLedger] = await PublicKey.findProgramAddress(
      [
        escrowSeed,
        clubTreasury.treasury.toBuffer(),
        tokenLedgerSeed,
        new PublicKey(proposal.metadata.mint).toBuffer(),
      ],
      escrowProgram.programId
    );
    let ledgerCount = 0;
    try {
      ledgerCount = (await escrowProgram.account.tokenLedger.fetch(tokenLedger))
        .ledgerEntriesCount;
    } catch (error) {
      console.log(error);
      ledgerCount = 0;
    }

    const ledgerBuffer = Buffer.alloc(4);
    ledgerBuffer.writeInt32LE(ledgerCount + 1, 0);

    const [ledgerEntry] = await PublicKey.findProgramAddress(
      [escrowSeed, tokenLedger.toBuffer(), ledgerBuffer],
      escrowProgram.programId
    );

    const recentBlockhash = await (
      await RPC_CONNECTION.getLatestBlockhash()
    ).blockhash;
    const tx = new Transaction({
      recentBlockhash: recentBlockhash,
      feePayer: wallet.publicKey,
    });

    const [profit] = await PublicKey.findProgramAddress(
      [unqClubSeed, clubTreasury.treasury.toBuffer(), profitSeed],
      clubProgram.programId
    );
    const [transferProfitAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
        transferProfitSeed,
      ],
      clubProgram.programId
    );

    const [memberPDA] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    const profitTokenAccounts =
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(profit, {
        mint: new PublicKey(proposal.metadata.mint),
      });

    if (profitTokenAccounts.value.length > 0) {
      const transferProfitIx = clubProgram.instruction.transferProfit(
        new BN(proposal.metadata.amountToInvest),
        {
          accounts: {
            clubData,
            payer: wallet.publicKey,
            treasury: clubTreasury.treasury,
            treasuryToken: proposal.metadata.treasuryToken,
            treasuryData: clubTreasury.treasuryDataAddress,
            profit,
            transferProfit: transferProfitAddress,
            proposal: proposal.proposalBasicData.proposalAccount.pubkey,
            profitToken: profitTokenAccounts.value[0].pubkey,
            memberData: memberPDA,
            systemProgram: web3.SystemProgram.programId,
            tokenProgram: TOKEN_PROGRAM_ID,
          },
        }
      );
      tx.add(transferProfitIx);
    }

    const executeTransferProposalIx =
      clubProgram.instruction.executeTransferProposal({
        accounts: {
          clubData,
          destination: proposal.metadata.destination,
          escrowProgram: escrowProgram.programId,
          governance:
            proposal.proposalBasicData.proposalAccount.account.governance,
          ledgerEntry,
          offer: proposal.metadata.offer,
          payer: wallet.publicKey,
          proposal: proposal.proposalBasicData.proposalAccount.pubkey,
          proposalMetadata: proposalMetadata,
          proposalTransaction: instructionAddress,
          realm: realmAddress,
          realmConfig,
          rent: SYSVAR_RENT_PUBKEY,
          splGovernanceProgram: splGovernanceProgram,
          systemProgram: SystemProgram.programId,
          tokenLedger,
          tokenProgram: TOKEN_PROGRAM_ID,
          treasury: clubTreasury.treasury,
          treasuryData,
          treasuryToken: proposal.metadata.treasuryToken,
        },
      });

    tx.add(executeTransferProposalIx);
    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet: wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getTradeInfoForAllVaultNfts = async (
  treasury: IClubTreasury,
  nftsFromVault: IDetailNftData[]
): Promise<INftsWithTradeInfo[]> => {
  try {
    const tokenLedgerAddresses: PublicKey[] = [];
    const ledgerEntryAddresses: PublicKey[] = [];
    const nftsWithCurrencyMint: INftsWithTradeInfo[] = [];
    const escrowProgram = escrowProgramFactory();
    for (const nft of nftsFromVault) {
      const [tokenLedger] = await PublicKey.findProgramAddress(
        [
          escrowSeed,
          treasury.treasury.toBuffer(),
          tokenLedgerSeed,
          nft.mint.toBuffer(),
        ],
        escrowProgram.programId
      );
      tokenLedgerAddresses.push(tokenLedger);
    }
    const tokenLedgerAccounts: any[] =
      await escrowProgram.account.tokenLedger.fetchMultiple(
        tokenLedgerAddresses
      );
    for (const [index, tokenLedger] of tokenLedgerAccounts.entries()) {
      if (tokenLedger) {
        const [ledgerEntry] = await PublicKey.findProgramAddress(
          [
            escrowSeed,
            tokenLedgerAddresses[index].toBuffer(),
            writeValueToNewInt32Buffer(tokenLedger.ledgerEntriesCount),
          ],
          escrowProgram.programId
        );
        ledgerEntryAddresses.push(ledgerEntry);
      }
    }

    const ledgerEntryAccounts: any[] =
      await escrowProgram.account.ledgerEntry.fetchMultiple(
        ledgerEntryAddresses
      );
    for (const nft of nftsFromVault) {
      const [tokenLedger] = await PublicKey.findProgramAddress(
        [
          escrowSeed,
          treasury.treasury.toBuffer(),
          tokenLedgerSeed,
          nft.mint.toBuffer(),
        ],
        escrowProgram.programId
      );

      const lastLedgerEntry = ledgerEntryAccounts.find(
        (item) =>
          item !== null &&
          item.tokenLedger.toString() === tokenLedger.toString()
      );

      if (lastLedgerEntry && lastLedgerEntry.buy.createdAt.toNumber() !== 0) {
        let mintSymbol: string;
        let mintDecimals: number;
        const treasuryToken = treasury.tokenAccounts.find(
          (item) => item.mint.toString() === lastLedgerEntry.buy.mint.toString()
        );
        if (!treasuryToken) {
          const mintInfo = await getInfoForMintAccounts([
            lastLedgerEntry.buy.mint,
          ]);
          mintSymbol = mintInfo[0].symbol;
          mintDecimals = mintInfo[0].decimals;
        } else {
          mintSymbol = treasuryToken.symbol;
          mintDecimals = treasuryToken.decimals;
        }
        nftsWithCurrencyMint.push({
          nftMint: nft.mint,
          tradeInfo: {
            currencyMint: lastLedgerEntry.buy.mint,
            price: lastLedgerEntry.buy.amount.toNumber(),
            mintSymbol,
            mintDecimals,
          },
        });
        continue;
      }
      nftsWithCurrencyMint.push({
        nftMint: nft.mint,
        tradeInfo: undefined,
      });
    }

    return nftsWithCurrencyMint;
  } catch (error) {
    console.log(error);
    throw error;
  }
};
